import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Switch, Typography, Space, Drawer, Form, Input, Select, Tag, message, Spin, Popconfirm, Modal, Table, Breadcrumb, Tabs, Radio, Alert, Divider, InputNumber, Result, DatePicker } from 'antd';
import {
  FileTextOutlined,
  MailOutlined,
  CalendarOutlined,
  FundProjectionScreenOutlined,
  FormOutlined,
  RobotOutlined,
  ShoppingCartOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SettingOutlined,
  SaveOutlined,
  CopyOutlined,
  ArrowLeftOutlined,
  FolderOutlined,
  FileOutlined,
  GoogleOutlined,
  LinkOutlined,
  DisconnectOutlined,
  ThunderboltOutlined,
  LoadingOutlined,
  EyeOutlined,
  CloudUploadOutlined,
  GlobalOutlined,
  FilePptOutlined,
  StopOutlined,
  FacebookOutlined,
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  VideoCameraOutlined,
  EditOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { scenarioAPI, gdriveAPI, videoAPI, marketingSiteAPI, contactsAPI, emailCampaignAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const STEP_TYPE_LABELS = {
  loi: '意向書',
  email_blast: '郵件行銷（三版本）',
  event_registration: '活動報名（三版本）',
  presentation: '簡報展示',
  survey: '問卷調查（三版本）',
  chatbot: 'AI 聊天機器人',
  sales_page: '銷售頁面',
  video: 'AI 視頻生成',
  blog: '部落格文章（三版本）',
};

const STEP_TYPE_ICONS = {
  loi: <FileTextOutlined />,
  email_blast: <MailOutlined />,
  event_registration: <CalendarOutlined />,
  presentation: <FundProjectionScreenOutlined />,
  survey: <FormOutlined />,
  chatbot: <RobotOutlined />,
  sales_page: <ShoppingCartOutlined />,
  video: <VideoCameraOutlined />,
  blog: <EditOutlined />,
};

const STATUS_CONFIG = {
  draft: { color: 'default', label: '草稿' },
  active: { color: 'green', label: '啟用中' },
  archived: { color: 'orange', label: '已封存' },
};

const ScenarioBuilderPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [scenario, setScenario] = useState(null);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [descValue, setDescValue] = useState('');

  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [stepForm] = Form.useForm();

  // Add step drawer
  const [addDrawerVisible, setAddDrawerVisible] = useState(false);
  const [addForm] = Form.useForm();

  // Google Drive folder picker
  const [gdriveModalOpen, setGdriveModalOpen] = useState(false);
  const [gdriveFolders, setGdriveFolders] = useState([]);
  const [gdriveLoading, setGdriveLoading] = useState(false);
  const [gdrivePath, setGdrivePath] = useState([]); // breadcrumb: [{id, name}]
  const [gdriveFiles, setGdriveFiles] = useState([]); // files in linked folder
  const [gdriveFilesLoading, setGdriveFilesLoading] = useState(false);
  const [gdriveSearch, setGdriveSearch] = useState('');

  // Step contents (latest generated content per step_type)
  const [stepContents, setStepContents] = useState({}); // { step_type: content }
  const [stepContentsLoading, setStepContentsLoading] = useState(false);

  // AI Generation state
  const [aiDrawerVisible, setAiDrawerVisible] = useState(false);
  const [aiStep, setAiStep] = useState(null); // which step is being generated
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiLanguage, setAiLanguage] = useState('zh-TW');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiFileContent, setAiFileContent] = useState('');
  const [aiFileLoading, setAiFileLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null); // generated content
  const [aiContents, setAiContents] = useState([]); // history of generated contents for this step
  // Presentation specific
  const [aiPptFolderId, setAiPptFolderId] = useState(null);
  const [aiPptFolderName, setAiPptFolderName] = useState('');
  const [aiPptFiles, setAiPptFiles] = useState([]);
  const [aiPptFilesLoading, setAiPptFilesLoading] = useState(false);
  const [aiExistingContentId, setAiExistingContentId] = useState(null); // for iterative refinement
  const [aiOutputFormat, setAiOutputFormat] = useState('web'); // 'web' or 'pptx'
  const [publishing, setPublishing] = useState(false);
  const [exportingPptx, setExportingPptx] = useState(false);
  const [pptxFolderPickerOpen, setPptxFolderPickerOpen] = useState(false);
  // Video generation state
  const [videoSourceVideos, setVideoSourceVideos] = useState([]);
  const [videoCategories, setVideoCategories] = useState([]);
  const [videoVoices, setVideoVoices] = useState([]);
  const [selectedSourceVideo, setSelectedSourceVideo] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState("中文 (女性-曉曉)");
  const [videoSourceLoading, setVideoSourceLoading] = useState(false);
  const [pptxTargetFolderId, setPptxTargetFolderId] = useState(null);
  const [pptxTargetFolderName, setPptxTargetFolderName] = useState('');
  // GDrive file picker for AI
  const [aiFilePickerOpen, setAiFilePickerOpen] = useState(false);
  const [aiFileBrowseItems, setAiFileBrowseItems] = useState([]);
  const [aiFileBrowseLoading, setAiFileBrowseLoading] = useState(false);
  const [aiFileBrowsePath, setAiFileBrowsePath] = useState([]);
  const [aiFileBrowseSearch, setAiFileBrowseSearch] = useState('');
  const [aiGdriveFolderId, setAiGdriveFolderId] = useState('');

  // Deploy as marketing site
  const [deployModalOpen, setDeployModalOpen] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState(null);
  const [existingSite, setExistingSite] = useState(null);

  // Import Google Slides
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFileId, setImportFileId] = useState('');
  const [importTitle, setImportTitle] = useState('');
  const [importStepType, setImportStepType] = useState('presentation');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importBrowseItems, setImportBrowseItems] = useState([]);
  const [importBrowseLoading, setImportBrowseLoading] = useState(false);
  const [importBrowsePath, setImportBrowsePath] = useState([]);
  const [importBrowseFolderId, setImportBrowseFolderId] = useState('');

  // Marketing publish state
  const [marketingModalOpen, setMarketingModalOpen] = useState(false);
  const [marketingPlatform, setMarketingPlatform] = useState('facebook'); // 'facebook' or 'google_ads'
  const [marketingPublishing, setMarketingPublishing] = useState(false);
  const [marketingContentId, setMarketingContentId] = useState(null);
  const [marketingResult, setMarketingResult] = useState(null);
  const [fbPostType, setFbPostType] = useState('page_post');
  const [marketingForm] = Form.useForm();

  // Blog publish state
  const [blogPublishModalOpen, setBlogPublishModalOpen] = useState(false);
  const [blogPublishContentId, setBlogPublishContentId] = useState(null);
  const [blogPublishing, setBlogPublishing] = useState(false);
  const [blogPublishResult, setBlogPublishResult] = useState(null);
  const [blogPlatforms, setBlogPlatforms] = useState({});
  const [blogSelectedPlatforms, setBlogSelectedPlatforms] = useState(['self_hosted']);
  const [blogPublishForm] = Form.useForm();

  // Email campaign state (multi-version email)
  const [emailVersionTab, setEmailVersionTab] = useState('sales');
  const [emailCampaignModalOpen, setEmailCampaignModalOpen] = useState(false);
  const [emailCampaignCreating, setEmailCampaignCreating] = useState(false);
  const [emailCampaignResult, setEmailCampaignResult] = useState(null);
  const [emailContactLists, setEmailContactLists] = useState([]);
  const [emailContactListsLoading, setEmailContactListsLoading] = useState(false);
  const [emailCampaignForm] = Form.useForm();
  const [presentationRef, setPresentationRef] = useState(null);

  // Event registration version tab
  const [eventVersionTab, setEventVersionTab] = useState('sales');
  const [eventPublishing, setEventPublishing] = useState(false);

  // Blog version tab + crawler
  const [blogVersionTab, setBlogVersionTab] = useState('sales');
  const [blogCrawlModalOpen, setBlogCrawlModalOpen] = useState(false);
  const [blogCrawlUrl, setBlogCrawlUrl] = useState('');
  const [blogCrawling, setBlogCrawling] = useState(false);
  const [crawledArticles, setCrawledArticles] = useState([]);
  const [selectedCrawledIds, setSelectedCrawledIds] = useState([]);
  const [blogCrawlGenerating, setBlogCrawlGenerating] = useState(false);

  // Survey version tab
  const [surveyVersionTab, setSurveyVersionTab] = useState('sales');

  const fetchScenario = useCallback(async () => {
    try {
      const res = await scenarioAPI.get(id);
      setScenario(res.data);
      setNameValue(res.data.name || '');
      setDescValue(res.data.description || '');
    } catch (error) {
      message.error('載入情境失敗：' + error.message);
    }
  }, [id]);

  const fetchSteps = useCallback(async () => {
    try {
      const res = await scenarioAPI.getSteps(id);
      setSteps(res.data || []);
    } catch (error) {
      message.error('載入步驟失敗：' + error.message);
    }
  }, [id]);

  const fetchStepContents = useCallback(async () => {
    setStepContentsLoading(true);
    try {
      const res = await scenarioAPI.listContents(id);
      const contents = res.data || [];
      // Group by step_type, keep the latest (first) for each
      const map = {};
      contents.forEach((c) => {
        if (!map[c.step_type]) {
          map[c.step_type] = c;
        }
      });
      setStepContents(map);
    } catch (e) {
      console.error('載入生成內容失敗:', e);
    } finally {
      setStepContentsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchScenario(), fetchSteps(), fetchStepContents()]);
      setLoading(false);
    };
    loadData();
  }, [fetchScenario, fetchSteps, fetchStepContents]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save scenario metadata
      await scenarioAPI.update(id, {
        name: nameValue,
        description: descValue,
      });
      // Save steps order and active state
      await scenarioAPI.updateSteps(id, {
        steps: steps.map((s, index) => ({
          id: s.id,
          step_order: index + 1,
          is_active: s.is_active,
        })),
      });
      message.success('儲存成功');
      fetchScenario();
    } catch (error) {
      message.error('儲存失敗：' + (error.message || '未知錯誤'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeploySite = async () => {
    setDeploying(true);
    setDeployResult(null);
    try {
      const res = await marketingSiteAPI.deploy(id, { title: scenario?.name });
      const result = res.data || res;
      setDeployResult(result);
      message.success('行銷網站部署成功！');
    } catch (error) {
      message.error('部署失敗：' + (error.message || '未知錯誤'));
    } finally {
      setDeploying(false);
    }
  };

  const handleRefreshSite = async () => {
    if (!existingSite?.id) return;
    setDeploying(true);
    try {
      await marketingSiteAPI.refresh(existingSite.id);
      message.success('行銷網站已更新為最新內容！');
      setDeployResult({
        public_url: existingSite.public_url || `/site/${existingSite.slug}`,
        refreshed: true,
      });
    } catch (error) {
      message.error('更新失敗：' + (error.message || '未知錯誤'));
    } finally {
      setDeploying(false);
    }
  };

  // Import Google Slides handlers
  const openImportModal = (stepType) => {
    setImportFileId('');
    setImportTitle('');
    setImportStepType(stepType || 'presentation');
    setImportResult(null);
    setImporting(false);
    setImportBrowseItems([]);
    setImportBrowsePath([]);
    setImportBrowseFolderId(scenario?.gdrive_folder_id || '');
    setImportModalOpen(true);
    // Auto-browse if we have a folder linked
    if (scenario?.gdrive_folder_id) {
      loadImportBrowse(scenario.gdrive_folder_id);
    }
  };

  const loadImportBrowse = async (folderId) => {
    if (!folderId) return;
    setImportBrowseLoading(true);
    try {
      const res = await gdriveAPI.browse(folderId);
      setImportBrowseItems((res.data || []).filter(f =>
        f.is_dir || f.name.toLowerCase().endsWith('.pptx') ||
        (f.mime_type && f.mime_type.includes('presentation'))
      ));
    } catch (e) {
      message.error('瀏覽失敗: ' + e.message);
    } finally {
      setImportBrowseLoading(false);
    }
  };

  const navigateImportBrowse = async (folderId, folderName) => {
    setImportBrowsePath(prev => [...prev, { id: folderId, name: folderName }]);
    await loadImportBrowse(folderId);
  };

  const navigateImportBreadcrumb = async (index) => {
    let folderId = importBrowseFolderId || scenario?.gdrive_folder_id || null;
    let newPath = [];
    if (index >= 0) {
      newPath = importBrowsePath.slice(0, index + 1);
      folderId = newPath[newPath.length - 1].id;
    }
    setImportBrowsePath(newPath);
    await loadImportBrowse(folderId);
  };

  const handleImportGSlides = async () => {
    if (!importFileId) {
      message.warning('請選擇或輸入 Google Drive 檔案 ID');
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const res = await scenarioAPI.importGSlides(id, {
        gdrive_file_id: importFileId,
        title: importTitle || undefined,
        step_type: importStepType,
      });
      setImportResult(res.data);
      // Update step contents
      setStepContents(prev => ({ ...prev, [importStepType]: res.data }));
      message.success('簡報匯入成功！');
      fetchStepContents();
    } catch (e) {
      message.error('匯入失敗: ' + e.message);
    } finally {
      setImporting(false);
    }
  };

  const handleClone = async () => {
    try {
      const res = await scenarioAPI.clone(id, {});
      message.success('情境複製成功');
      if (res.data && res.data.id) {
        navigate(`/scenarios/${res.data.id}`);
      }
    } catch (error) {
      message.error('複製失敗：' + error.message);
    }
  };

  const handleToggleStep = (stepId, checked) => {
    setSteps((prev) =>
      prev.map((s) => (s.id === stepId ? { ...s, is_active: checked } : s))
    );
  };

  const handleMoveStep = (index, direction) => {
    const newSteps = [...steps];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newSteps.length) return;
    [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
    setSteps(newSteps);
  };

  const handleConfigStep = (step) => {
    setEditingStep(step);
    stepForm.setFieldsValue({
      name: step.name || '',
      config: step.config ? JSON.stringify(step.config, null, 2) : '{}',
    });
    setDrawerVisible(true);
  };

  const handleSaveStepConfig = async () => {
    try {
      const values = await stepForm.validateFields();
      let configObj = {};
      try {
        configObj = JSON.parse(values.config);
      } catch {
        message.error('設定 JSON 格式錯誤');
        return;
      }
      await scenarioAPI.updateStep(id, editingStep.id, {
        name: values.name,
        config: configObj,
      });
      message.success('步驟設定已儲存');
      setDrawerVisible(false);
      fetchSteps();
    } catch (error) {
      if (error.errorFields) return;
      message.error('儲存步驟設定失敗：' + error.message);
    }
  };

  const handleAddStep = async () => {
    try {
      const values = await addForm.validateFields();
      // Add step via updateSteps with new step appended
      const newStep = {
        step_type: values.step_type,
        name: values.name || STEP_TYPE_LABELS[values.step_type],
        sort_order: steps.length,
        is_active: true,
        config: {},
      };
      const updatedSteps = [
        ...steps.map((s, i) => ({ id: s.id, sort_order: i, is_active: s.is_active })),
        newStep,
      ];
      await scenarioAPI.updateSteps(id, { steps: updatedSteps });
      message.success('步驟已新增');
      setAddDrawerVisible(false);
      addForm.resetFields();
      fetchSteps();
    } catch (error) {
      if (error.errorFields) return;
      message.error('新增步驟失敗：' + error.message);
    }
  };

  // AI Generation functions
  const AI_STEP_TYPES = ['loi', 'email_blast', 'event_registration', 'presentation', 'survey', 'chatbot', 'sales_page', 'video', 'blog'];

  const openAiDrawer = async (step) => {
    setAiStep(step);
    setAiDrawerVisible(true);
    setAiLanguage('zh-TW');
    setAiPrompt('');
    setAiFileContent('');
    setAiResult(null);
    setAiExistingContentId(null);
    setAiOutputFormat(step.step_type === 'presentation' ? 'pptx' : 'web');
    setPptxTargetFolderId(null);
    setPptxTargetFolderName('');
    setAiPptFolderId(scenario?.gdrive_folder_id || null);
    setAiPptFolderName(scenario?.gdrive_folder_name || '');
    setAiPptFiles([]);
    // Load existing generated contents for this step
    try {
      const res = await scenarioAPI.listContents(id);
      const allContents = res.data || [];
      const filtered = allContents.filter(c => c.step_type === step.step_type);
      setAiContents(filtered);
      // Auto-detect presentation content for multi-version step types
      const presStepTypes = ['email_blast', 'event_registration', 'blog', 'survey', 'sales_page'];
      if (presStepTypes.includes(step.step_type)) {
        const pres = allContents.find(c => c.step_type === 'presentation');
        setPresentationRef(pres || null);
        if (step.step_type === 'email_blast') setEmailVersionTab('sales');
        if (step.step_type === 'event_registration') setEventVersionTab('sales');
        if (step.step_type === 'blog') setBlogVersionTab('sales');
        if (step.step_type === 'survey') setSurveyVersionTab('sales');
      } else {
        setPresentationRef(null);
      }
    } catch (e) {
      setAiContents([]);
      setPresentationRef(null);
    }
    // For presentation, load folder files
    // For video, load source videos and voices
    if (step.step_type === "video") {
      setSelectedSourceVideo(null);
      setSelectedVoice("中文 (女性-曉曉)");
      setVideoSourceLoading(true);
      try {
        const [videosRes, catsRes, voicesRes] = await Promise.all([
          videoAPI.getSourceVideos({ limit: 50 }),
          videoAPI.getCategories(),
          videoAPI.getVoices(),
        ]);
        setVideoSourceVideos(videosRes.data?.data || []);
        setVideoCategories(catsRes.data?.data || []);
        setVideoVoices(voicesRes.data || []);
      } catch (e) { console.error("Load video data failed:", e); }
      finally { setVideoSourceLoading(false); }
    }
    if (step.step_type === 'presentation' && scenario?.gdrive_folder_id) {
      loadAiPptFiles(scenario.gdrive_folder_id);
    }
  };

  const loadAiPptFiles = async (folderId) => {
    if (!folderId) return;
    setAiPptFilesLoading(true);
    try {
      const res = await gdriveAPI.browse(folderId);
      setAiPptFiles(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setAiPptFilesLoading(false);
    }
  };

  const loadGdriveFileContent = async (fileId, fileName) => {
    setAiFileLoading(true);
    try {
      const res = await gdriveAPI.getFileContent(fileId, fileName);
      setAiFileContent(prev => prev ? prev + '\n\n---\n\n' + res.data.content : res.data.content);
      message.success(`已載入: ${res.data.filename}`);
    } catch (e) {
      message.error('讀取檔案失敗: ' + e.message);
    } finally {
      setAiFileLoading(false);
    }
  };

  // File picker for AI
  const openAiFilePicker = async (customFolderId) => {
    setAiFilePickerOpen(true);
    setAiFileBrowsePath([]);
    setAiFileBrowseSearch('');
    const folderId = customFolderId || aiGdriveFolderId || aiPptFolderId || scenario?.gdrive_folder_id || null;
    setAiFileBrowseLoading(true);
    try {
      const res = await gdriveAPI.browse(folderId);
      setAiFileBrowseItems(res.data || []);
    } catch (e) {
      message.error('載入失敗，請確認資料夾 ID 是否正確');
    } finally {
      setAiFileBrowseLoading(false);
    }
  };

  const navigateAiFileBrowse = async (folderId, folderName) => {
    setAiFileBrowsePath(prev => [...prev, { id: folderId, name: folderName }]);
    setAiFileBrowseLoading(true);
    setAiFileBrowseSearch('');
    try {
      const res = await gdriveAPI.browse(folderId);
      setAiFileBrowseItems(res.data || []);
    } catch (e) {
      message.error('載入失敗');
    } finally {
      setAiFileBrowseLoading(false);
    }
  };

  const navigateAiFileBreadcrumb = async (index) => {
    let folderId = aiGdriveFolderId || aiPptFolderId || scenario?.gdrive_folder_id || null;
    let newPath = [];
    if (index >= 0) {
      newPath = aiFileBrowsePath.slice(0, index + 1);
      folderId = newPath[newPath.length - 1].id;
    }
    setAiFileBrowsePath(newPath);
    setAiFileBrowseLoading(true);
    setAiFileBrowseSearch('');
    try {
      const res = await gdriveAPI.browse(folderId);
      setAiFileBrowseItems(res.data || []);
    } catch (e) {
      message.error('載入失敗');
    } finally {
      setAiFileBrowseLoading(false);
    }
  };

  const handleAiGenerate = async () => {
    // For video step, validate source video selection
    if (aiStep?.step_type === 'video') {
      if (!selectedSourceVideo) {
        message.warning('請先選擇人物視頻');
        return;
      }
      if (!aiPrompt) {
        message.warning('請輸入要生成的語音文字');
        return;
      }
    } else if (!aiPrompt && !aiFileContent) {
      message.warning('請輸入提示字或載入檔案內容');
      return;
    }
    setAiGenerating(true);
    try {
      let payload = {
        product_info: aiPrompt,
        language: aiLanguage,
        file_content: aiFileContent || undefined,
        existing_content_id: aiExistingContentId || undefined,
        output_format: aiOutputFormat,
      };
      // For video step, add source_video_id and voice to context
      if (aiStep?.step_type === 'video') {
        payload.context = JSON.stringify({
          source_video_id: selectedSourceVideo,
          voice: selectedVoice,
        });
      }
      const res = await scenarioAPI.generate(id, aiStep.step_type, payload);
      setAiResult(res.data);
      setAiContents(prev => [res.data, ...prev]);
      setAiExistingContentId(res.data.id);
      // Update step contents map so the step card shows latest
      setStepContents(prev => ({ ...prev, [aiStep.step_type]: res.data }));
      message.success('AI 生成完成！');
    } catch (e) {
      message.error('AI 生成失敗: ' + e.message);
    } finally {
      setAiGenerating(false);
    }
  };

  // Publish / Export handlers
  const getPublicUrl = (token) => {
    if (!token) return '';
    const origin = window.location.origin;
    return `${origin}/p/${token}`;
  };

  const handlePublish = async (contentId) => {
    setPublishing(true);
    try {
      const res = await scenarioAPI.publishContent(id, contentId);
      const updated = res.data;
      setAiResult(updated);
      setAiContents(prev => prev.map(c => c.id === updated.id ? updated : c));
      setStepContents(prev => ({ ...prev, [updated.step_type]: updated }));
      message.success('已發布為網頁');
    } catch (e) {
      message.error('發布失敗: ' + e.message);
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async (contentId) => {
    setPublishing(true);
    try {
      const res = await scenarioAPI.unpublishContent(id, contentId);
      const updated = res.data;
      setAiResult(updated);
      setAiContents(prev => prev.map(c => c.id === updated.id ? updated : c));
      setStepContents(prev => ({ ...prev, [updated.step_type]: updated }));
      message.success('已取消發布');
    } catch (e) {
      message.error('取消發布失敗: ' + e.message);
    } finally {
      setPublishing(false);
    }
  };

  const handleExportPptx = async (contentId, folderId) => {
    const targetFolder = folderId || pptxTargetFolderId || scenario?.gdrive_folder_id;
    if (!targetFolder) {
      message.warning('請先選擇 Google Drive 資料夾');
      return;
    }
    setExportingPptx(true);
    try {
      const res = await scenarioAPI.exportPptx(id, contentId, { gdrive_folder_id: targetFolder });
      const updated = res.data;
      setAiResult(updated);
      setAiContents(prev => prev.map(c => c.id === updated.id ? updated : c));
      message.success(`PPTX 已上傳至 Google Drive: ${updated.pptx_filename}`);
    } catch (e) {
      message.error('PPTX 匯出失敗: ' + e.message);
    } finally {
      setExportingPptx(false);
    }
  };

  const handlePptxToWeb = async (contentId) => {
    setPublishing(true);
    try {
      const res = await scenarioAPI.pptxToWeb(id, contentId);
      const updated = res.data;
      setAiResult(updated);
      setAiContents(prev => prev.map(c => c.id === updated.id ? updated : c));
      setStepContents(prev => ({ ...prev, [updated.step_type]: updated }));
      message.success('已轉換為網頁並發布');
    } catch (e) {
      message.error('轉換失敗: ' + e.message);
    } finally {
      setPublishing(false);
    }
  };

  // Marketing Publish handlers
  const openMarketingModal = (contentId, platform) => {
    setMarketingContentId(contentId);
    setMarketingPlatform(platform);
    setMarketingResult(null);
    setFbPostType('page_post');
    marketingForm.resetFields();

    // Pre-fill form from aiResult
    if (aiResult) {
      const raw = aiResult.raw_content || {};
      if (platform === 'facebook') {
        marketingForm.setFieldsValue({
          message: aiResult.title || '',
          campaign_name: `SPO - ${aiResult.title || ''}`,
          headline: (raw.headline || aiResult.title || '').substring(0, 60),
          daily_budget_cents: 1000,
        });
      } else {
        const headlines = [];
        if (aiResult.title) headlines.push(aiResult.title.substring(0, 30));
        if (raw.headline) headlines.push(String(raw.headline).substring(0, 30));
        if (raw.subject) headlines.push(String(raw.subject).substring(0, 30));

        marketingForm.setFieldsValue({
          campaign_name: `SPO - ${aiResult.title || ''}`,
          headlines: headlines.join('\n') || '',
          descriptions: (raw.subheadline || raw.welcome_message || '').substring(0, 90),
          keywords: aiResult.title || '',
          daily_budget_micros: 10000000,
        });
      }
      // Set link URL if published
      if (aiResult.public_token) {
        const pubUrl = getPublicUrl(aiResult.public_token);
        marketingForm.setFieldsValue({ link: pubUrl, final_url: pubUrl });
      }
    }
    setMarketingModalOpen(true);
  };

  const handleMarketingPublish = async () => {
    setMarketingPublishing(true);
    setMarketingResult(null);
    try {
      const values = marketingForm.getFieldsValue();
      let res;

      if (marketingPlatform === 'facebook') {
        res = await scenarioAPI.publishToFacebook(id, marketingContentId, {
          content_id: marketingContentId,
          post_type: fbPostType,
          message: values.message,
          link: values.link,
          campaign_name: values.campaign_name,
          headline: values.headline,
          daily_budget_cents: values.daily_budget_cents,
          description: values.description,
        });
      } else {
        // Parse headlines/descriptions/keywords from textarea
        const headlines = (values.headlines || '').split('\n').map(s => s.trim()).filter(Boolean);
        const descriptions = (values.descriptions || '').split('\n').map(s => s.trim()).filter(Boolean);
        const keywords = (values.keywords || '').split('\n').map(s => s.trim()).filter(Boolean);

        res = await scenarioAPI.publishToGoogleAds(id, marketingContentId, {
          content_id: marketingContentId,
          campaign_name: values.campaign_name,
          headlines,
          descriptions,
          final_url: values.final_url,
          keywords,
          daily_budget_micros: values.daily_budget_micros,
        });
      }

      setMarketingResult(res.data);
      if (res.data.ok) {
        message.success(res.data.message || '發布成功');
      } else {
        message.error(res.data.error || '發布失敗');
      }
    } catch (e) {
      setMarketingResult({ ok: false, error: e.message });
      message.error('發布失敗: ' + e.message);
    } finally {
      setMarketingPublishing(false);
    }
  };

  // Blog multi-platform publish handlers
  const openBlogPublishModal = async (contentId) => {
    setBlogPublishContentId(contentId);
    setBlogPublishResult(null);
    setBlogSelectedPlatforms(['self_hosted']);
    blogPublishForm.resetFields();

    // Pre-fill from aiResult
    if (aiResult) {
      const raw = aiResult.raw_content || {};
      blogPublishForm.setFieldsValue({
        message: raw.social_post || aiResult.title || '',
        tags: (raw.tags || []).join(', '),
        category: '',
      });
    }

    // Load platform status
    try {
      const res = await scenarioAPI.getBlogPlatforms(id, contentId);
      setBlogPlatforms(res.data || {});
    } catch (e) {
      console.error('Load blog platforms failed:', e);
    }
    setBlogPublishModalOpen(true);
  };

  const handleBlogPublish = async () => {
    setBlogPublishing(true);
    setBlogPublishResult(null);
    try {
      const values = blogPublishForm.getFieldsValue();
      const tags = (values.tags || '').split(',').map(t => t.trim()).filter(Boolean);
      const res = await scenarioAPI.publishBlog(id, blogPublishContentId, {
        content_id: blogPublishContentId,
        platforms: blogSelectedPlatforms,
        message: values.message || undefined,
        tags: tags.length > 0 ? tags : undefined,
        category: values.category || undefined,
      });
      setBlogPublishResult(res.data);
      if (res.data.ok) {
        message.success('部落格發布成功！');
      } else {
        const failedPlatforms = (res.data.results || []).filter(r => !r.ok).map(r => r.platform).join(', ');
        message.warning(`部分平台發布失敗：${failedPlatforms}`);
      }
    } catch (e) {
      setBlogPublishResult({ ok: false, error: e.message });
      message.error('發布失敗: ' + e.message);
    } finally {
      setBlogPublishing(false);
    }
  };

  // Email campaign functions
  const openEmailCampaignModal = async (contentId) => {
    setEmailCampaignModalOpen(true);
    setEmailCampaignResult(null);
    setEmailCampaignCreating(false);
    emailCampaignForm.resetFields();
    // Load contact lists
    setEmailContactListsLoading(true);
    try {
      const res = await contactsAPI.listLists();
      setEmailContactLists(res.data || []);
    } catch (e) {
      setEmailContactLists([]);
    } finally {
      setEmailContactListsLoading(false);
    }
  };

  const handleCreateEmailCampaign = async (sendNow = false) => {
    if (!aiResult?.id) return;
    setEmailCampaignCreating(true);
    try {
      const values = emailCampaignForm.getFieldsValue();
      const payload = {
        email_version: emailVersionTab,
        contact_list_id: values.contact_list_id,
        name: values.campaign_name || undefined,
      };
      if (!sendNow && values.scheduled_at) {
        payload.scheduled_at = values.scheduled_at.toISOString();
      }
      const res = await scenarioAPI.createEmailCampaign(id, aiResult.id, payload);
      if (res.data?.success) {
        if (sendNow && res.data.campaign_id) {
          await emailCampaignAPI.send(res.data.campaign_id);
          message.success('郵件已送出！');
        } else if (payload.scheduled_at) {
          message.success(`郵件已排程，將於 ${values.scheduled_at.format('YYYY-MM-DD HH:mm')} 自動發送`);
        } else {
          message.success('郵件活動已建立（草稿）');
        }
        setEmailCampaignResult(res.data);
      }
    } catch (e) {
      message.error('建立郵件活動失敗: ' + e.message);
    } finally {
      setEmailCampaignCreating(false);
    }
  };

  const publishEventPage = async (content) => {
    if (!content?.id || !content.raw_content?.versions) return;
    setEventPublishing(true);
    try {
      const res = await scenarioAPI.publishVersion(id, content.id, { version: eventVersionTab });
      if (res.data?.success) {
        const pubUrl = `${window.location.origin}/p/${res.data.public_token}`;
        Modal.success({
          title: '報名頁面已發布',
          content: (
            <div>
              <p><strong>{res.data.title}</strong></p>
              <p>公開網址：</p>
              <Input value={pubUrl} readOnly addonAfter={
                <span style={{ cursor: 'pointer' }} onClick={() => { navigator.clipboard.writeText(pubUrl); message.success('已複製連結'); }}>複製</span>
              } />
            </div>
          ),
        });
        fetchStepContents();
      }
    } catch (e) {
      message.error('發布失敗: ' + e.message);
    } finally {
      setEventPublishing(false);
    }
  };

  const publishVersionPage = async (content, versionKey) => {
    if (!content?.id || !content.raw_content?.versions) return;
    setEventPublishing(true);
    try {
      const res = await scenarioAPI.publishVersion(id, content.id, { version: versionKey });
      if (res.data?.success) {
        const pubUrl = `${window.location.origin}/p/${res.data.public_token}`;
        Modal.success({
          title: '頁面已發布',
          content: (
            <div>
              <p><strong>{res.data.title}</strong></p>
              <p>公開網址：</p>
              <Input value={pubUrl} readOnly addonAfter={
                <span style={{ cursor: 'pointer' }} onClick={() => { navigator.clipboard.writeText(pubUrl); message.success('已複製連結'); }}>複製</span>
              } />
            </div>
          ),
        });
        fetchStepContents();
      }
    } catch (e) {
      message.error('發布失敗: ' + e.message);
    } finally {
      setEventPublishing(false);
    }
  };

  const handleBlogCrawl = async () => {
    if (!blogCrawlUrl) { message.warning('請輸入部落格網址'); return; }
    setBlogCrawling(true);
    try {
      const res = await scenarioAPI.blogCrawl(id, { url: blogCrawlUrl, max_articles: 10 });
      setCrawledArticles(res.data?.articles || []);
      setSelectedCrawledIds([]);
      if ((res.data?.articles || []).length === 0) message.info('未找到文章');
      else message.success(`已爬取 ${res.data.articles.length} 篇文章`);
    } catch (e) {
      message.error('爬取失敗: ' + e.message);
    } finally {
      setBlogCrawling(false);
    }
  };

  const handleGenerateFromCrawled = async () => {
    if (selectedCrawledIds.length === 0) { message.warning('請先選取參考文章'); return; }
    setBlogCrawlGenerating(true);
    try {
      const res = await scenarioAPI.blogCrawlGenerate(id, {
        article_ids: selectedCrawledIds,
        prompt: aiPrompt || '根據參考文章生成新的行銷部落格',
      });
      if (res.data?.id) {
        message.success('已根據爬取文章生成新部落格');
        setBlogCrawlModalOpen(false);
        fetchStepContents();
      }
    } catch (e) {
      message.error('生成失敗: ' + e.message);
    } finally {
      setBlogCrawlGenerating(false);
    }
  };

  // Google Drive functions
  const openGdrivePicker = async () => {
    setGdriveModalOpen(true);
    setGdrivePath([]);
    setGdriveSearch('');
    await loadGdriveFolders(null);
  };

  const loadGdriveFolders = async (parentId) => {
    setGdriveLoading(true);
    try {
      const res = await gdriveAPI.listFolders(parentId);
      setGdriveFolders(res.data || []);
    } catch (e) {
      message.error('載入 Google Drive 失敗: ' + e.message);
    } finally {
      setGdriveLoading(false);
    }
  };

  const navigateGdriveFolder = async (folderId, folderName) => {
    setGdrivePath(prev => [...prev, { id: folderId, name: folderName }]);
    await loadGdriveFolders(folderId);
  };

  const navigateGdriveBreadcrumb = async (index) => {
    if (index < 0) {
      setGdrivePath([]);
      await loadGdriveFolders(null);
    } else {
      const newPath = gdrivePath.slice(0, index + 1);
      setGdrivePath(newPath);
      await loadGdriveFolders(newPath[newPath.length - 1].id);
    }
  };

  const selectGdriveFolder = async (folderId, folderName) => {
    try {
      await scenarioAPI.update(id, {
        gdrive_folder_id: folderId,
        gdrive_folder_name: folderName,
      });
      setScenario(prev => ({ ...prev, gdrive_folder_id: folderId, gdrive_folder_name: folderName }));
      message.success(`已連結 Google Drive: ${folderName}`);
      setGdriveModalOpen(false);
      loadGdriveLinkedFiles(folderId);
    } catch (e) {
      message.error('設定失敗: ' + e.message);
    }
  };

  const unlinkGdrive = async () => {
    try {
      await scenarioAPI.update(id, {
        gdrive_folder_id: '',
        gdrive_folder_name: '',
      });
      setScenario(prev => ({ ...prev, gdrive_folder_id: null, gdrive_folder_name: null }));
      setGdriveFiles([]);
      message.success('已取消連結');
    } catch (e) {
      message.error('取消失敗: ' + e.message);
    }
  };

  const loadGdriveLinkedFiles = async (folderId) => {
    if (!folderId) return;
    setGdriveFilesLoading(true);
    try {
      const res = await gdriveAPI.browse(folderId);
      setGdriveFiles(res.data || []);
    } catch (e) {
      console.error('Load GDrive files failed:', e);
    } finally {
      setGdriveFilesLoading(false);
    }
  };

  // Load linked GDrive files on scenario load
  useEffect(() => {
    if (scenario?.gdrive_folder_id) {
      loadGdriveLinkedFiles(scenario.gdrive_folder_id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario?.gdrive_folder_id]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!scenario) {
    return (
      <Card>
        <Text type="danger">無法載入情境資料</Text>
        <br />
        <Button type="link" onClick={() => navigate('/scenarios')}>
          返回情境列表
        </Button>
      </Card>
    );
  }

  const statusConfig = STATUS_CONFIG[scenario.status] || { color: 'default', label: scenario.status };

  return (
    <div>
      {/* Header Card */}
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space align="center">
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/scenarios')}
              >
                返回列表
              </Button>
              {editingName ? (
                <Input
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  onBlur={() => setEditingName(false)}
                  onPressEnter={() => setEditingName(false)}
                  style={{ fontSize: 20, fontWeight: 600, width: 400 }}
                  autoFocus
                />
              ) : (
                <Title
                  level={4}
                  style={{ margin: 0, cursor: 'pointer' }}
                  onClick={() => setEditingName(true)}
                >
                  {nameValue || '未命名情境'}
                </Title>
              )}
              <Tag color={statusConfig.color}>{statusConfig.label}</Tag>
            </Space>
            <Space>
              <Button
                icon={<GlobalOutlined />}
                type="primary"
                ghost
                onClick={async () => {
                  setDeployResult(null);
                  setDeployModalOpen(true);
                  try {
                    const res = await marketingSiteAPI.list();
                    const sites = (res.data || res || []).filter(s => s.scenario_id === parseInt(id));
                    setExistingSite(sites.length > 0 ? sites[0] : null);
                  } catch (e) { setExistingSite(null); }
                }}
              >
                部署行銷網站
              </Button>
              <Button icon={<CopyOutlined />} onClick={handleClone}>
                複製情境
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
              >
                儲存
              </Button>
            </Space>
          </Space>
          <Paragraph
            editable={{
              onChange: setDescValue,
              text: descValue,
            }}
            style={{ marginBottom: 0, color: '#666' }}
          >
            {descValue || '點擊新增描述...'}
          </Paragraph>
        </Space>
      </Card>

      {/* Pipeline Steps */}
      <Card
        title="流程步驟"
        extra={
          <Button
            type="dashed"
            icon={<FormOutlined />}
            onClick={() => setAddDrawerVisible(true)}
          >
            新增步驟
          </Button>
        }
      >
        {steps.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            尚未新增任何步驟，請點擊「新增步驟」開始建立流程
          </div>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {steps.map((step, index) => (
              <Card
                key={step.id}
                size="small"
                style={{
                  borderLeft: step.is_active ? '4px solid #1890ff' : '4px solid #d9d9d9',
                  opacity: step.is_active ? 1 : 0.6,
                }}
              >
                <Space style={{ width: '100%', justifyContent: 'space-between' }} align="center">
                  <Space align="center" size="middle">
                    <span style={{ fontSize: 24, color: '#1890ff' }}>
                      {STEP_TYPE_ICONS[step.step_type] || <FormOutlined />}
                    </span>
                    <div>
                      <Text strong style={{ fontSize: 15 }}>
                        {step.name || STEP_TYPE_LABELS[step.step_type] || step.step_type}
                      </Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {STEP_TYPE_LABELS[step.step_type] || step.step_type}
                      </Text>
                    </div>
                  </Space>
                  <Space>
                    <Switch
                      checked={step.is_active}
                      onChange={(checked) => handleToggleStep(step.id, checked)}
                      checkedChildren="啟用"
                      unCheckedChildren="停用"
                    />
                    <Button
                      size="small"
                      icon={<ArrowUpOutlined />}
                      disabled={index === 0}
                      onClick={() => handleMoveStep(index, -1)}
                    />
                    <Button
                      size="small"
                      icon={<ArrowDownOutlined />}
                      disabled={index === steps.length - 1}
                      onClick={() => handleMoveStep(index, 1)}
                    />
                    {AI_STEP_TYPES.includes(step.step_type) && (
                      <Button
                        size="small"
                        type="primary"
                        ghost
                        icon={<ThunderboltOutlined />}
                        onClick={() => openAiDrawer(step)}
                      >
                        AI 生成
                      </Button>
                    )}
                    <Button
                      size="small"
                      icon={<CloudUploadOutlined />}
                      onClick={() => openImportModal(step.step_type)}
                    >
                      匯入簡報
                    </Button>
                    <Button
                      size="small"
                      icon={<SettingOutlined />}
                      onClick={() => handleConfigStep(step)}
                    >
                      設定
                    </Button>
                  </Space>
                </Space>

                {/* AI Generated Content Preview */}
                {stepContents[step.step_type] && (
                  <div style={{
                    marginTop: 12,
                    padding: 12,
                    background: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    borderRadius: 6,
                  }}>
                    <Space style={{ marginBottom: 8, width: '100%', justifyContent: 'space-between' }}>
                      <Space>
                        <ThunderboltOutlined style={{ color: '#52c41a' }} />
                        <Text strong style={{ fontSize: 13, color: '#52c41a' }}>
                          AI 生成內容
                        </Text>
                        <Tag color="blue" style={{ fontSize: 11 }}>
                          {stepContents[step.step_type].title}
                        </Tag>
                        <Tag style={{ fontSize: 11 }}>
                          v{stepContents[step.step_type].version}
                        </Tag>
                        {stepContents[step.step_type].is_published && (
                          <Tag color="green" style={{ fontSize: 11 }}>已發布</Tag>
                        )}
                        {stepContents[step.step_type].pptx_filename && (
                          <Tag color="orange" style={{ fontSize: 11 }}>PPTX</Tag>
                        )}
                      </Space>
                      <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => openAiDrawer(step)}
                      >
                        查看完整內容
                      </Button>
                    </Space>
                    <div
                      style={{
                        maxHeight: 150,
                        overflow: 'hidden',
                        fontSize: 13,
                        lineHeight: 1.6,
                        color: '#333',
                        position: 'relative',
                      }}
                    >
                      <div dangerouslySetInnerHTML={{
                        __html: stepContents[step.step_type].html_content
                          ? stepContents[step.step_type].html_content.substring(0, 500)
                          : ''
                      }} />
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: 40,
                        background: 'linear-gradient(transparent, #f6ffed)',
                      }} />
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </Space>
        )}
      </Card>

      {/* Google Drive Folder */}
      <Card
        title={<Space><GoogleOutlined /> Google Drive 資料夾</Space>}
        style={{ marginTop: 16 }}
        extra={
          scenario?.gdrive_folder_id ? (
            <Space>
              <Button size="small" icon={<LinkOutlined />} onClick={openGdrivePicker}>更換</Button>
              <Popconfirm title="確認取消連結？" onConfirm={unlinkGdrive}>
                <Button size="small" danger icon={<DisconnectOutlined />}>取消連結</Button>
              </Popconfirm>
            </Space>
          ) : (
            <Button type="primary" icon={<FolderOutlined />} onClick={openGdrivePicker}>
              選擇資料夾
            </Button>
          )
        }
      >
        {scenario?.gdrive_folder_id ? (
          <div>
            <Space style={{ marginBottom: 12 }}>
              <FolderOutlined style={{ color: '#faad14', fontSize: 18 }} />
              <Text strong>{scenario.gdrive_folder_name || scenario.gdrive_folder_id}</Text>
              <Text type="secondary" copyable={{ text: scenario.gdrive_folder_id }}>
                ID: {scenario.gdrive_folder_id.substring(0, 20)}...
              </Text>
            </Space>
            <Table
              dataSource={gdriveFiles}
              rowKey="id"
              size="small"
              loading={gdriveFilesLoading}
              pagination={{ pageSize: 10 }}
              columns={[
                {
                  title: '名稱', dataIndex: 'name',
                  render: (name, record) => (
                    <Space>
                      {record.is_dir ? <FolderOutlined style={{ color: '#faad14' }} /> : <FileOutlined style={{ color: '#1890ff' }} />}
                      <Text>{name}</Text>
                    </Space>
                  ),
                },
                { title: '大小', dataIndex: 'size_human', width: 100 },
                {
                  title: '修改時間', dataIndex: 'mod_time', width: 160,
                  render: (v) => v ? new Date(v).toLocaleString('zh-TW') : '-',
                },
              ]}
            />
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            <FolderOutlined style={{ fontSize: 40, marginBottom: 8 }} /><br />
            尚未連結 Google Drive 資料夾<br />
            <Text type="secondary">連結後可瀏覽行銷相關文件</Text>
          </div>
        )}
      </Card>

      {/* Google Drive Folder Picker Modal */}
      <Modal
        title="選擇 Google Drive 資料夾"
        open={gdriveModalOpen}
        onCancel={() => setGdriveModalOpen(false)}
        footer={null}
        width={650}
      >
        <Breadcrumb style={{ marginBottom: 12 }}>
          <Breadcrumb.Item>
            <Button type="link" size="small" onClick={() => navigateGdriveBreadcrumb(-1)}>My Drive</Button>
          </Breadcrumb.Item>
          {gdrivePath.map((p, idx) => (
            <Breadcrumb.Item key={p.id}>
              <Button type="link" size="small" onClick={() => navigateGdriveBreadcrumb(idx)}>{p.name}</Button>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>

        <Input.Search
          placeholder="搜尋資料夾名稱..."
          allowClear
          value={gdriveSearch}
          onChange={(e) => setGdriveSearch(e.target.value)}
          style={{ marginBottom: 12 }}
        />

        {gdrivePath.length > 0 && (
          <Button
            type="primary"
            size="small"
            style={{ marginBottom: 12 }}
            onClick={() => {
              const current = gdrivePath[gdrivePath.length - 1];
              selectGdriveFolder(current.id, current.name);
            }}
          >
            選擇此資料夾：{gdrivePath[gdrivePath.length - 1]?.name}
          </Button>
        )}

        <Table
          dataSource={gdriveSearch ? gdriveFolders.filter(f => f.name.toLowerCase().includes(gdriveSearch.toLowerCase())) : gdriveFolders}
          rowKey="id"
          size="small"
          loading={gdriveLoading}
          pagination={{ pageSize: 15 }}
          onRow={(record) => ({
            onDoubleClick: () => navigateGdriveFolder(record.id, record.name),
            style: { cursor: 'pointer' },
          })}
          columns={[
            {
              title: '資料夾名稱', dataIndex: 'name',
              render: (name) => (
                <Space>
                  <FolderOutlined style={{ color: '#faad14' }} />
                  <Text>{name}</Text>
                </Space>
              ),
            },
            {
              title: '操作', width: 160,
              render: (_, record) => (
                <Space>
                  <Button size="small" onClick={() => navigateGdriveFolder(record.id, record.name)}>
                    進入
                  </Button>
                  <Button size="small" type="primary" onClick={() => selectGdriveFolder(record.id, record.name)}>
                    選擇
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Modal>

      {/* AI Generation Drawer */}
      <Drawer
        title={
          <Space>
            <ThunderboltOutlined />
            AI 生成 - {aiStep ? (STEP_TYPE_LABELS[aiStep.step_type] || aiStep.step_type) : ''}
          </Space>
        }
        open={aiDrawerVisible}
        onClose={() => setAiDrawerVisible(false)}
        width={750}
      >
        {aiStep && (
          <div>
            {/* Language Selection */}
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>語言選擇</Text>
              <Radio.Group value={aiLanguage} onChange={(e) => setAiLanguage(e.target.value)}>
                <Radio.Button value="zh-TW">繁體中文</Radio.Button>
                <Radio.Button value="en">English</Radio.Button>
                <Radio.Button value="th">ภาษาไทย</Radio.Button>
              </Radio.Group>
            </div>

            {/* Output Format Selection — hidden for video */}
            {aiStep.step_type !== 'video' && <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>輸出格式</Text>
              <Radio.Group value={aiOutputFormat} onChange={(e) => setAiOutputFormat(e.target.value)}>
                <Radio.Button value="web">
                  <GlobalOutlined /> 網頁格式
                </Radio.Button>
                <Radio.Button value="pptx">
                  <FilePptOutlined /> 簡報格式 (PPTX)
                </Radio.Button>
              </Radio.Group>
              <div style={{ marginTop: 6 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {aiOutputFormat === 'web'
                    ? '生成後可發布為網頁，透過 Token 安全網址分享'
                    : '生成後可匯出為 PPTX 簡報，上傳至 Google Drive，並可轉換為網頁'}
                </Text>
              </div>
            </div>}

            {/* Presentation: Step 1 - Google Drive folder */}
            {aiStep.step_type === 'presentation' && (
              <Card size="small" style={{ marginBottom: 16 }} title="步驟一：簡報資料來源 (Google Drive)">
                {aiPptFolderId ? (
                  <div>
                    <Space style={{ marginBottom: 8 }}>
                      <FolderOutlined style={{ color: '#faad14' }} />
                      <Text strong>{aiPptFolderName}</Text>
                    </Space>
                    <Table
                      dataSource={aiPptFiles.filter(f => !f.is_dir)}
                      rowKey="id"
                      size="small"
                      loading={aiPptFilesLoading}
                      pagination={false}
                      style={{ marginTop: 8 }}
                      columns={[
                        {
                          title: '檔案', dataIndex: 'name',
                          render: (name) => <Space><FileOutlined style={{ color: '#1890ff' }} /><Text>{name}</Text></Space>,
                        },
                        { title: '大小', dataIndex: 'size_human', width: 100 },
                        {
                          title: '', width: 80,
                          render: (_, record) => (
                            <Button
                              size="small"
                              loading={aiFileLoading}
                              onClick={() => loadGdriveFileContent(record.id, record.name)}
                            >
                              載入
                            </Button>
                          ),
                        },
                      ]}
                    />
                  </div>
                ) : (
                  <Alert
                    type="info"
                    message="請先在情境設定中連結 Google Drive 資料夾"
                    showIcon
                  />
                )}
              </Card>
            )}

            {/* Video: Source Video Picker & Voice Selector */}
            {aiStep.step_type === 'video' && (
              <Card size="small" style={{ marginBottom: 16 }} title="步驟一：選擇人物視頻">
                {videoSourceLoading ? (
                  <div style={{ textAlign: 'center', padding: 24 }}><Spin tip="載入視頻..." /></div>
                ) : videoSourceVideos.length === 0 ? (
                  <Alert type="warning" message="沒有可用的人物視頻，請先在 VideoAI 管理器中上傳視頻" showIcon />
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12, maxHeight: 360, overflowY: 'auto', padding: 4 }}>
                    {videoSourceVideos.map(v => (
                      <div
                        key={v.id}
                        onClick={() => setSelectedSourceVideo(v.id)}
                        style={{
                          border: selectedSourceVideo === v.id ? '3px solid #1890ff' : '2px solid #d9d9d9',
                          borderRadius: 8,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          background: selectedSourceVideo === v.id ? '#e6f7ff' : '#fff',
                          transition: 'all 0.2s',
                        }}
                      >
                        <img
                          src={`/api/v1/video/source-videos/${v.id}/thumbnail`}
                          alt={v.original_filename}
                          style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div style={{ padding: '4px 6px', fontSize: 11, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {v.original_filename?.replace(/\.[^.]+$/, '') || `視頻 ${v.id}`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {aiStep.step_type === 'video' && (
              <Card size="small" style={{ marginBottom: 16 }} title="步驟二：選擇語音">
                <Select
                  style={{ width: '100%' }}
                  value={selectedVoice}
                  onChange={setSelectedVoice}
                  placeholder="選擇 TTS 語音"
                >
                  {videoVoices.map(v => (
                    <Option key={v} value={v}>{v}</Option>
                  ))}
                </Select>
              </Card>
            )}

            {/* Presentation reference indicator for multi-version steps */}
            {['email_blast', 'event_registration', 'blog', 'survey', 'sales_page'].includes(aiStep.step_type) && presentationRef && (
              <Alert
                type="success"
                showIcon
                icon={<FundProjectionScreenOutlined />}
                style={{ marginBottom: 16 }}
                message={`已自動載入簡報內容：「${presentationRef.title}」`}
                description={
                  aiStep.step_type === 'email_blast' ? "系統將根據此簡報內容自動生成三種版本的郵件（銷售版、行銷版、活動版），可直接排程發送" :
                  aiStep.step_type === 'event_registration' ? "系統將根據此簡報內容自動生成三種版本的活動報名頁（銷售版、教育訓練付費版、教育訓練免費版），可發布為線上報名頁面" :
                  aiStep.step_type === 'blog' ? "系統將根據此簡報內容自動生成三種版本的部落格文章（銷售導向版、SEO長文版、社群分享版）" :
                  aiStep.step_type === 'survey' ? "系統將根據此簡報內容自動生成三種版本的問卷（銷售導向版、滿意度調查版、需求調查版）" :
                  "系統將根據此簡報內容自動生成專業銷售頁面，包含痛點分析、產品特色、客戶見證、價格方案等區塊"
                }
              />
            )}
            {['email_blast', 'event_registration', 'blog', 'survey', 'sales_page'].includes(aiStep.step_type) && !presentationRef && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="此情境尚未生成簡報內容"
                description="建議先在簡報步驟生成或匯入內容，系統將自動引用作為生成的素材。您也可以直接輸入產品資訊來生成。"
              />
            )}

            {/* Prompt Input */}
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                {aiStep.step_type === 'video' ? '步驟三：輸入語音文字' : aiStep.step_type === 'presentation' ? '步驟二：輸入提示字' : '輸入提示字'}
              </Text>
              {aiExistingContentId && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message="修改模式：請在下方輸入您要修改的內容指示，例如「修改第 3 頁，加入更多產品規格」「將結論頁改為英文」等"
                />
              )}
              {!aiExistingContentId && aiContents.length > 0 && (
                <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                  如需修改已生成的內容，請在下方「步驟三」選擇要修改的版本，然後在此輸入修改指示
                </Text>
              )}
              <Input.TextArea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={4}
                placeholder={
                  aiExistingContentId
                    ? '請輸入修改指示，例如：修改第 2 頁加入更多技術細節、將標題改為...、新增一頁關於客戶案例...'
                  : aiStep.step_type === 'loi' ? '例：針對某公司合作的意向書，合作項目為 HCI 超融合基礎架構部署...' :
                  aiStep.step_type === 'email_blast' ? '例：Aeon AI 教育訓練平台推廣郵件（系統將自動讀取簡報內容，生成銷售版/行銷版/活動版三版郵件）...' :
                  aiStep.step_type === 'event_registration' ? '例：Aeon AI 教育訓練平台體驗活動（系統將自動讀取簡報內容，生成銷售版/付費培訓版/免費體驗版三版報名頁）...' :
                  aiStep.step_type === 'presentation' ? '例：Comboware HCI 超融合基礎架構產品簡報，包含產品特色、技術規格、客戶案例...' :
                  aiStep.step_type === 'survey' ? '例：Aeon AI 教育訓練平台問卷（系統將自動讀取簡報內容，生成銷售導向版/滿意度調查版/需求調查版三版問卷）...' :
                  aiStep.step_type === 'chatbot' ? '例：產品諮詢聊天機器人，能回答 HCI 產品規格、價格、技術支援相關問題...' :
                  aiStep.step_type === 'sales_page' ? '例：Aeon AI 教育訓練平台銷售首頁（系統將自動讀取簡報內容，生成專業銷售頁面含痛點分析、產品特色、價格方案）...' :
                  aiStep.step_type === 'video' ? '輸入要生成的語音文字，例如：大家好，歡迎來到 Comboware，今天為您介紹我們最新的 AI 伺服器產品...' :
                  aiStep.step_type === 'blog' ? '例：Aeon AI 教育訓練平台推廣文章（系統將自動讀取簡報內容，生成銷售導向版/SEO長文版/社群分享版三版部落格）...' :
                  '請輸入提示字...'
                }
              />
            </div>

            {/* File content from GDrive — hidden for video */}
            {aiStep.step_type !== 'video' && <div style={{ marginBottom: 16 }}>
              <Space style={{ marginBottom: 8 }}>
                <Text strong>參考檔案內容</Text>
                <Button size="small" icon={<FolderOutlined />} onClick={openAiFilePicker}>
                  從 Google Drive 選擇檔案
                </Button>
                {aiFileContent && (
                  <Button size="small" danger onClick={() => setAiFileContent('')}>清除</Button>
                )}
              </Space>
              {aiFileContent && (
                <Input.TextArea
                  value={aiFileContent}
                  onChange={(e) => setAiFileContent(e.target.value)}
                  rows={6}
                  style={{ fontFamily: 'monospace', fontSize: 12 }}
                />
              )}
            </div>}

            {/* Iterative refinement — select existing content to modify */}
            {aiContents.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  {aiStep.step_type === 'presentation' ? '步驟三：' : ''}選擇既有內容進行修改（持續迭代）
                </Text>
                <Select
                  style={{ width: '100%' }}
                  allowClear
                  placeholder="選擇要修改的版本（選擇後在上方輸入修改指示）"
                  value={aiExistingContentId}
                  onChange={setAiExistingContentId}
                  options={aiContents.map(c => ({
                    value: c.id,
                    label: `#${c.id} ${c.title} (v${c.version}) - ${c.created_at ? new Date(c.created_at).toLocaleString('zh-TW') : ''}`,
                  }))}
                />
              </div>
            )}

            {/* Generate Button */}
            <Button
              type="primary"
              size="large"
              icon={aiGenerating ? <LoadingOutlined /> : <ThunderboltOutlined />}
              onClick={handleAiGenerate}
              loading={aiGenerating}
              block
              style={{ marginBottom: 12 }}
            >
              {aiGenerating ? 'AI 生成中...' : aiStep?.step_type === 'video' ? '生成 AI 口播視頻' : aiStep?.step_type === 'blog' ? (aiExistingContentId ? 'AI 修改部落格' : 'AI 撰寫部落格') : aiExistingContentId ? 'AI 依指示修改內容' : 'AI 生成內容'}
            </Button>

            {/* Import Google Slides shortcut */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Divider style={{ margin: '0 0 12px' }}><Text type="secondary" style={{ fontSize: 12 }}>或</Text></Divider>
              <Button
                size="large"
                icon={<CloudUploadOutlined />}
                onClick={() => { setAiDrawerVisible(false); openImportModal(aiStep?.step_type || 'presentation'); }}
                block
              >
                匯入 Google 簡報 / PPTX（不需 AI 生成）
              </Button>
            </div>

            {/* Result Preview */}
            {aiResult && (
              <Card
                size="small"
                title={
                  <Space>
                    <EyeOutlined />
                    生成結果：{aiResult.title}
                    <Tag color="blue">v{aiResult.version}</Tag>
                    {aiResult.output_format === 'pptx' && <Tag color="orange">PPTX</Tag>}
                    {aiResult.is_published && <Tag color="green">已發布</Tag>}
                  </Space>
                }
                style={{ marginBottom: 16 }}
              >
                {/* Action Buttons */}
                <div style={{ marginBottom: 12, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Space wrap>
                    {/* Publish as web page */}
                    {!aiResult.is_published ? (
                      <Button
                        type="primary"
                        size="small"
                        icon={<GlobalOutlined />}
                        loading={publishing}
                        onClick={() => handlePublish(aiResult.id)}
                      >
                        發布為網頁
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        danger
                        icon={<StopOutlined />}
                        loading={publishing}
                        onClick={() => handleUnpublish(aiResult.id)}
                      >
                        取消發布
                      </Button>
                    )}

                    {/* Export as PPTX */}
                    <Button
                      size="small"
                      icon={<FilePptOutlined />}
                      loading={exportingPptx}
                      onClick={() => handleExportPptx(aiResult.id)}
                      disabled={!scenario?.gdrive_folder_id && !pptxTargetFolderId}
                    >
                      {aiResult.pptx_filename ? '重新匯出 PPTX' : '匯出 PPTX 至 Google Drive'}
                    </Button>

                    {/* PPTX to Web */}
                    {aiResult.pptx_filename && !aiResult.is_published && (
                      <Button
                        size="small"
                        icon={<GlobalOutlined />}
                        loading={publishing}
                        onClick={() => handlePptxToWeb(aiResult.id)}
                      >
                        PPTX 轉網頁發布
                      </Button>
                    )}

                    <Divider type="vertical" />

                    {/* Post to Facebook */}
                    <Button
                      size="small"
                      icon={<FacebookOutlined />}
                      style={{ color: '#1877F2', borderColor: '#1877F2' }}
                      onClick={() => openMarketingModal(aiResult.id, 'facebook')}
                    >
                      發布到 Facebook
                    </Button>

                    {/* Post to Google Ads */}
                    <Button
                      size="small"
                      icon={<GoogleOutlined />}
                      style={{ color: '#4285F4', borderColor: '#4285F4' }}
                      onClick={() => openMarketingModal(aiResult.id, 'google_ads')}
                    >
                      發布到 Google Ads
                    </Button>

                    {/* Blog multi-platform publish */}
                    {aiStep?.step_type === 'blog' && (
                      <>
                        <Divider type="vertical" />
                        <Button
                          size="small"
                          type="primary"
                          icon={<SendOutlined />}
                          style={{ background: '#52c41a', borderColor: '#52c41a' }}
                          onClick={() => openBlogPublishModal(aiResult.id)}
                        >
                          發布部落格（多平台）
                        </Button>
                      </>
                    )}

                    {/* Email campaign: schedule & send */}
                    {aiStep?.step_type === 'email_blast' && aiResult.raw_content?.versions && (
                      <>
                        <Divider type="vertical" />
                        <Button
                          size="small"
                          type="primary"
                          icon={<MailOutlined />}
                          onClick={() => openEmailCampaignModal(aiResult.id)}
                        >
                          排程發送郵件
                        </Button>
                      </>
                    )}
                    {aiStep?.step_type === 'event_registration' && aiResult.raw_content?.versions && (
                      <>
                        <Divider type="vertical" />
                        <Button
                          size="small"
                          type="primary"
                          icon={<GlobalOutlined />}
                          onClick={() => publishEventPage(aiResult)}
                          loading={eventPublishing}
                        >
                          發布為報名頁面
                        </Button>
                      </>
                    )}
                    {aiStep?.step_type === 'blog' && aiResult.raw_content?.versions && (
                      <>
                        <Divider type="vertical" />
                        <Button
                          size="small"
                          type="primary"
                          icon={<GlobalOutlined />}
                          onClick={() => publishVersionPage(aiResult, blogVersionTab)}
                          loading={eventPublishing}
                        >
                          發布為網頁
                        </Button>
                        <Divider type="vertical" />
                        <Button
                          size="small"
                          icon={<SearchOutlined />}
                          onClick={() => setBlogCrawlModalOpen(true)}
                        >
                          爬取相關文章
                        </Button>
                      </>
                    )}
                    {aiStep?.step_type === 'survey' && aiResult.raw_content?.versions && (
                      <>
                        <Divider type="vertical" />
                        <Button
                          size="small"
                          type="primary"
                          icon={<GlobalOutlined />}
                          onClick={() => publishVersionPage(aiResult, surveyVersionTab)}
                          loading={eventPublishing}
                        >
                          發布為問卷頁面
                        </Button>
                      </>
                    )}
                    {aiStep?.step_type === 'sales_page' && (
                      <>
                        <Divider type="vertical" />
                        <Button
                          size="small"
                          type="primary"
                          icon={<GlobalOutlined />}
                          onClick={() => handlePublish(aiResult.id)}
                          loading={publishing}
                        >
                          發布為銷售頁面
                        </Button>
                      </>
                    )}
                  </Space>

                  {/* Published URL */}
                  {aiResult.is_published && aiResult.public_token && (
                    <div style={{ marginTop: 8, padding: '8px 12px', background: '#f6ffed', borderRadius: 6, border: '1px solid #b7eb8f' }}>
                      <Space>
                        <GlobalOutlined style={{ color: '#52c41a' }} />
                        <Text strong style={{ fontSize: 12, color: '#52c41a' }}>公開網址：</Text>
                        <Text copyable={{ text: getPublicUrl(aiResult.public_token) }} style={{ fontSize: 12 }}>
                          {getPublicUrl(aiResult.public_token)}
                        </Text>
                      </Space>
                    </div>
                  )}

                  {/* PPTX info */}
                  {aiResult.pptx_filename && (
                    <div style={{ marginTop: 8, padding: '8px 12px', background: '#fff7e6', borderRadius: 6, border: '1px solid #ffd591' }}>
                      <Space>
                        <FilePptOutlined style={{ color: '#fa8c16' }} />
                        <Text style={{ fontSize: 12 }}>
                          Google Drive: <Text strong style={{ fontSize: 12 }}>{aiResult.pptx_filename}</Text>
                        </Text>
                      </Space>
                    </div>
                  )}
                </div>

                {/* Event Registration multi-version tabs */}
                {aiStep?.step_type === 'event_registration' && aiResult.raw_content?.versions ? (
                  <Tabs
                    activeKey={eventVersionTab}
                    onChange={setEventVersionTab}
                    items={[
                      { key: 'sales', label: <span style={{ color: '#e74c3c' }}>💼 銷售版</span>,
                        children: (() => { const v = aiResult.raw_content.versions.sales || {}; return (
                          <div>
                            <div style={{ padding: '10px 14px', background: '#fff1f0', borderRadius: 8, marginBottom: 12 }}>
                              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{v.event_name || '(未命名)'}</div>
                              <Space split={<span style={{ color: '#d9d9d9' }}>|</span>} wrap>
                                {v.date && <Text type="secondary">📅 {v.date}</Text>}
                                {v.location && <Text type="secondary">📍 {v.location}</Text>}
                                {v.price && <Text type="secondary">💰 {v.price}</Text>}
                              </Space>
                              {v.description && <div style={{ marginTop: 6, color: '#666', fontSize: 13 }}>{v.description.substring(0, 200)}</div>}
                            </div>
                            <div style={{ maxHeight: 350, overflow: 'auto', border: '1px solid #f0f0f0', padding: 12, borderRadius: 8 }}
                              dangerouslySetInnerHTML={{ __html: v.html_body || '' }} />
                          </div>
                        ); })(),
                      },
                      { key: 'paid_training', label: <span style={{ color: '#9b59b6' }}>🎓 付費培訓版</span>,
                        children: (() => { const v = aiResult.raw_content.versions.paid_training || {}; return (
                          <div>
                            <div style={{ padding: '10px 14px', background: '#f9f0ff', borderRadius: 8, marginBottom: 12 }}>
                              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{v.event_name || '(未命名)'}</div>
                              <Space split={<span style={{ color: '#d9d9d9' }}>|</span>} wrap>
                                {v.date && <Text type="secondary">📅 {v.date}</Text>}
                                {v.location && <Text type="secondary">📍 {v.location}</Text>}
                                {v.price && <Text type="secondary">💰 {v.price}</Text>}
                                {v.early_bird_price && <Text style={{ color: '#9b59b6' }}>🏷️ 早鳥 {v.early_bird_price}</Text>}
                              </Space>
                              {v.description && <div style={{ marginTop: 6, color: '#666', fontSize: 13 }}>{v.description.substring(0, 200)}</div>}
                            </div>
                            <div style={{ maxHeight: 350, overflow: 'auto', border: '1px solid #f0f0f0', padding: 12, borderRadius: 8 }}
                              dangerouslySetInnerHTML={{ __html: v.html_body || '' }} />
                          </div>
                        ); })(),
                      },
                      { key: 'free_training', label: <span style={{ color: '#2ecc71' }}>🆓 免費體驗版</span>,
                        children: (() => { const v = aiResult.raw_content.versions.free_training || {}; return (
                          <div>
                            <div style={{ padding: '10px 14px', background: '#f6ffed', borderRadius: 8, marginBottom: 12 }}>
                              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{v.event_name || '(未命名)'}</div>
                              <Space split={<span style={{ color: '#d9d9d9' }}>|</span>} wrap>
                                {v.date && <Text type="secondary">📅 {v.date}</Text>}
                                {v.location && <Text type="secondary">📍 {v.location}</Text>}
                                <Text style={{ color: '#52c41a', fontWeight: 600 }}>🆓 免費</Text>
                                {v.capacity && <Text type="secondary">👥 限額 {v.capacity}</Text>}
                              </Space>
                              {v.description && <div style={{ marginTop: 6, color: '#666', fontSize: 13 }}>{v.description.substring(0, 200)}</div>}
                            </div>
                            <div style={{ maxHeight: 350, overflow: 'auto', border: '1px solid #f0f0f0', padding: 12, borderRadius: 8 }}
                              dangerouslySetInnerHTML={{ __html: v.html_body || '' }} />
                          </div>
                        ); })(),
                      },
                      { key: 'raw', label: '結構化資料',
                        children: (
                          <pre style={{ maxHeight: 400, overflow: 'auto', fontSize: 12, background: '#f5f5f5', padding: 12, borderRadius: 8 }}>
                            {JSON.stringify(aiResult.raw_content, null, 2)}
                          </pre>
                        ),
                      },
                    ]}
                  />
                ) : /* Blog multi-version tabs */
                aiStep?.step_type === 'blog' && aiResult.raw_content?.versions ? (
                  <Tabs
                    activeKey={blogVersionTab}
                    onChange={setBlogVersionTab}
                    items={[
                      { key: 'sales', label: <span style={{ color: '#e74c3c' }}>🔥 銷售導向版</span>,
                        children: (() => { const v = aiResult.raw_content.versions.sales || {}; return (
                          <div>
                            <div style={{ padding: '10px 14px', background: '#fff1f0', borderRadius: 8, marginBottom: 12 }}>
                              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{v.title || '(未命名)'}</div>
                              {v.excerpt && <div style={{ color: '#666', fontSize: 13, marginBottom: 6 }}>{v.excerpt}</div>}
                              <Space wrap>
                                {(v.tags || []).map(t => <Tag key={t} color="red">{t}</Tag>)}
                              </Space>
                            </div>
                            <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #f0f0f0', padding: 16, borderRadius: 8 }}
                              dangerouslySetInnerHTML={{ __html: v.html_body || '' }} />
                          </div>
                        ); })(),
                      },
                      { key: 'seo', label: <span style={{ color: '#3498db' }}>📊 SEO 長文版</span>,
                        children: (() => { const v = aiResult.raw_content.versions.seo || {}; return (
                          <div>
                            <div style={{ padding: '10px 14px', background: '#e6f7ff', borderRadius: 8, marginBottom: 12 }}>
                              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{v.title || '(未命名)'}</div>
                              {v.seo_description && <div style={{ color: '#666', fontSize: 13, marginBottom: 6 }}>SEO：{v.seo_description}</div>}
                              <Space wrap>
                                {(v.tags || []).map(t => <Tag key={t} color="blue">{t}</Tag>)}
                              </Space>
                            </div>
                            <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #f0f0f0', padding: 16, borderRadius: 8 }}
                              dangerouslySetInnerHTML={{ __html: v.html_body || '' }} />
                          </div>
                        ); })(),
                      },
                      { key: 'social', label: <span style={{ color: '#2ecc71' }}>📱 社群分享版</span>,
                        children: (() => { const v = aiResult.raw_content.versions.social || {}; return (
                          <div>
                            <div style={{ padding: '10px 14px', background: '#f6ffed', borderRadius: 8, marginBottom: 12 }}>
                              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{v.title || '(未命名)'}</div>
                              {v.social_post && <div style={{ color: '#52c41a', fontSize: 13, marginBottom: 6, padding: '6px 10px', background: '#e6fffb', borderRadius: 6 }}>📣 {v.social_post}</div>}
                              <Space wrap>
                                {(v.tags || []).map(t => <Tag key={t} color="green">{t}</Tag>)}
                              </Space>
                            </div>
                            <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #f0f0f0', padding: 16, borderRadius: 8 }}
                              dangerouslySetInnerHTML={{ __html: v.html_body || '' }} />
                          </div>
                        ); })(),
                      },
                      { key: 'raw', label: '結構化資料',
                        children: (
                          <pre style={{ maxHeight: 400, overflow: 'auto', fontSize: 12, background: '#f5f5f5', padding: 12, borderRadius: 8 }}>
                            {JSON.stringify(aiResult.raw_content, null, 2)}
                          </pre>
                        ),
                      },
                    ]}
                  />
                ) : /* Survey multi-version tabs */
                aiStep?.step_type === 'survey' && aiResult.raw_content?.versions ? (
                  <Tabs
                    activeKey={surveyVersionTab}
                    onChange={setSurveyVersionTab}
                    items={[
                      { key: 'sales', label: <span style={{ color: '#e74c3c' }}>💼 銷售導向版</span>,
                        children: (() => { const v = aiResult.raw_content.versions.sales || {}; return (
                          <div>
                            <div style={{ padding: '10px 14px', background: '#fff1f0', borderRadius: 8, marginBottom: 12 }}>
                              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{v.survey_name || '(未命名)'}</div>
                              {v.target_audience && <Text type="secondary">🎯 對象：{v.target_audience}</Text>}
                              {v.description && <div style={{ marginTop: 6, color: '#666', fontSize: 13 }}>{v.description}</div>}
                            </div>
                            <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #f0f0f0', padding: 16, borderRadius: 8 }}
                              dangerouslySetInnerHTML={{ __html: v.html_body || '' }} />
                          </div>
                        ); })(),
                      },
                      { key: 'satisfaction', label: <span style={{ color: '#faad14' }}>⭐ 滿意度調查版</span>,
                        children: (() => { const v = aiResult.raw_content.versions.satisfaction || {}; return (
                          <div>
                            <div style={{ padding: '10px 14px', background: '#fffbe6', borderRadius: 8, marginBottom: 12 }}>
                              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{v.survey_name || '(未命名)'}</div>
                              {v.target_audience && <Text type="secondary">🎯 對象：{v.target_audience}</Text>}
                              {v.description && <div style={{ marginTop: 6, color: '#666', fontSize: 13 }}>{v.description}</div>}
                            </div>
                            <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #f0f0f0', padding: 16, borderRadius: 8 }}
                              dangerouslySetInnerHTML={{ __html: v.html_body || '' }} />
                          </div>
                        ); })(),
                      },
                      { key: 'needs', label: <span style={{ color: '#9b59b6' }}>📋 需求調查版</span>,
                        children: (() => { const v = aiResult.raw_content.versions.needs || {}; return (
                          <div>
                            <div style={{ padding: '10px 14px', background: '#f9f0ff', borderRadius: 8, marginBottom: 12 }}>
                              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{v.survey_name || '(未命名)'}</div>
                              {v.target_audience && <Text type="secondary">🎯 對象：{v.target_audience}</Text>}
                              {v.description && <div style={{ marginTop: 6, color: '#666', fontSize: 13 }}>{v.description}</div>}
                            </div>
                            <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #f0f0f0', padding: 16, borderRadius: 8 }}
                              dangerouslySetInnerHTML={{ __html: v.html_body || '' }} />
                          </div>
                        ); })(),
                      },
                      { key: 'raw', label: '結構化資料',
                        children: (
                          <pre style={{ maxHeight: 400, overflow: 'auto', fontSize: 12, background: '#f5f5f5', padding: 12, borderRadius: 8 }}>
                            {JSON.stringify(aiResult.raw_content, null, 2)}
                          </pre>
                        ),
                      },
                    ]}
                  />
                ) : /* Email multi-version tabs */
                aiStep?.step_type === 'email_blast' && aiResult.raw_content?.versions ? (
                  <Tabs
                    activeKey={emailVersionTab}
                    onChange={setEmailVersionTab}
                    items={[
                      { key: 'sales', label: <span style={{ color: '#e74c3c' }}>🔥 銷售版</span>,
                        children: (() => { const v = aiResult.raw_content.versions.sales || {}; return (
                          <div>
                            <div style={{ padding: '8px 12px', background: '#fff1f0', borderRadius: 6, marginBottom: 12 }}>
                              <Text strong>主旨：</Text> {v.subject || '(無)'}
                              {v.preview_text && <><br /><Text type="secondary">預覽：{v.preview_text}</Text></>}
                            </div>
                            <div style={{ maxHeight: 350, overflow: 'auto', border: '1px solid #f0f0f0', padding: 12, borderRadius: 8 }}
                              dangerouslySetInnerHTML={{ __html: v.html_body || '' }} />
                          </div>
                        ); })(),
                      },
                      { key: 'marketing', label: <span style={{ color: '#3498db' }}>📢 行銷版</span>,
                        children: (() => { const v = aiResult.raw_content.versions.marketing || {}; return (
                          <div>
                            <div style={{ padding: '8px 12px', background: '#e6f7ff', borderRadius: 6, marginBottom: 12 }}>
                              <Text strong>主旨：</Text> {v.subject || '(無)'}
                              {v.preview_text && <><br /><Text type="secondary">預覽：{v.preview_text}</Text></>}
                            </div>
                            <div style={{ maxHeight: 350, overflow: 'auto', border: '1px solid #f0f0f0', padding: 12, borderRadius: 8 }}
                              dangerouslySetInnerHTML={{ __html: v.html_body || '' }} />
                          </div>
                        ); })(),
                      },
                      { key: 'event', label: <span style={{ color: '#2ecc71' }}>🎉 活動版</span>,
                        children: (() => { const v = aiResult.raw_content.versions.event || {}; return (
                          <div>
                            <div style={{ padding: '8px 12px', background: '#f6ffed', borderRadius: 6, marginBottom: 12 }}>
                              <Text strong>主旨：</Text> {v.subject || '(無)'}
                              {v.preview_text && <><br /><Text type="secondary">預覽：{v.preview_text}</Text></>}
                            </div>
                            <div style={{ maxHeight: 350, overflow: 'auto', border: '1px solid #f0f0f0', padding: 12, borderRadius: 8 }}
                              dangerouslySetInnerHTML={{ __html: v.html_body || '' }} />
                          </div>
                        ); })(),
                      },
                      { key: 'raw', label: '結構化資料',
                        children: (
                          <pre style={{ maxHeight: 400, overflow: 'auto', fontSize: 12, background: '#f5f5f5', padding: 12, borderRadius: 8 }}>
                            {JSON.stringify(aiResult.raw_content, null, 2)}
                          </pre>
                        ),
                      },
                    ]}
                  />
                ) : (
                  <Tabs items={[
                    {
                      key: 'preview', label: '預覽',
                      children: aiResult.raw_content?.video_url ? (
                        <div style={{ textAlign: 'center', padding: 16 }}>
                          <video
                            controls
                            style={{ maxWidth: '100%', maxHeight: 360, borderRadius: 8 }}
                            src={aiResult.raw_content.video_url}
                          >
                            您的瀏覽器不支援視頻播放
                          </video>
                          <div style={{ marginTop: 8 }}>
                            <Space>
                              <Text type="secondary">語音: {aiResult.raw_content.voice}</Text>
                              <a href={aiResult.raw_content.video_url} download>
                                <Button size="small" icon={<CloudUploadOutlined />}>下載視頻</Button>
                              </a>
                            </Space>
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #f0f0f0', padding: 16, borderRadius: 8 }}
                          dangerouslySetInnerHTML={{ __html: aiResult.html_content || '' }}
                        />
                      ),
                    },
                    {
                      key: 'raw', label: '結構化資料',
                      children: (
                        <pre style={{ maxHeight: 400, overflow: 'auto', fontSize: 12, background: '#f5f5f5', padding: 12, borderRadius: 8 }}>
                          {JSON.stringify(aiResult.raw_content, null, 2)}
                        </pre>
                      ),
                    },
                  ]} />
                )}
              </Card>
            )}

            {/* Generated Content History */}
            {aiContents.length > 0 && (
              <Card size="small" title="生成歷史">
                <Table
                  dataSource={aiContents}
                  rowKey="id"
                  size="small"
                  pagination={{ pageSize: 5 }}
                  columns={[
                    { title: 'ID', dataIndex: 'id', width: 50 },
                    { title: '標題', dataIndex: 'title', ellipsis: true },
                    { title: '版本', dataIndex: 'version', width: 60, render: (v) => `v${v}` },
                    {
                      title: '狀態', width: 100,
                      render: (_, record) => (
                        <Space size={4}>
                          {record.is_published && <Tag color="green" style={{ fontSize: 11 }}>網頁</Tag>}
                          {record.pptx_filename && <Tag color="orange" style={{ fontSize: 11 }}>PPTX</Tag>}
                          {!record.is_published && !record.pptx_filename && <Tag style={{ fontSize: 11 }}>草稿</Tag>}
                        </Space>
                      ),
                    },
                    {
                      title: '時間', dataIndex: 'created_at', width: 140,
                      render: (v) => v ? new Date(v).toLocaleString('zh-TW') : '-',
                    },
                    {
                      title: '', width: 60,
                      render: (_, record) => (
                        <Button size="small" icon={<EyeOutlined />} onClick={() => setAiResult(record)}>查看</Button>
                      ),
                    },
                  ]}
                />
              </Card>
            )}
          </div>
        )}
      </Drawer>

      {/* AI File Picker Modal */}
      <Modal
        title="從 Google Drive 選擇檔案"
        open={aiFilePickerOpen}
        onCancel={() => setAiFilePickerOpen(false)}
        footer={null}
        width={650}
      >
        <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          <GoogleOutlined style={{ color: '#4285F4', fontSize: 16 }} />
          <Input
            placeholder="貼上 Google Drive 資料夾 ID（從網址複製）"
            value={aiGdriveFolderId}
            onChange={(e) => setAiGdriveFolderId(e.target.value.trim())}
            onPressEnter={() => openAiFilePicker(aiGdriveFolderId)}
            style={{ flex: 1 }}
            allowClear
          />
          <Button
            type="primary"
            size="small"
            onClick={() => openAiFilePicker(aiGdriveFolderId)}
            disabled={!aiGdriveFolderId}
          >
            前往
          </Button>
        </div>
        <Breadcrumb style={{ marginBottom: 12 }}>
          <Breadcrumb.Item>
            <Button type="link" size="small" onClick={() => navigateAiFileBreadcrumb(-1)}>
              {aiGdriveFolderId ? '指定資料夾' : '根目錄'}
            </Button>
          </Breadcrumb.Item>
          {aiFileBrowsePath.map((p, idx) => (
            <Breadcrumb.Item key={p.id}>
              <Button type="link" size="small" onClick={() => navigateAiFileBreadcrumb(idx)}>{p.name}</Button>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
        <Input.Search
          placeholder="搜尋..."
          allowClear
          value={aiFileBrowseSearch}
          onChange={(e) => setAiFileBrowseSearch(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <Table
          dataSource={
            aiFileBrowseSearch
              ? aiFileBrowseItems.filter(f => f.name.toLowerCase().includes(aiFileBrowseSearch.toLowerCase()))
              : aiFileBrowseItems
          }
          rowKey="id"
          size="small"
          loading={aiFileBrowseLoading}
          pagination={{ pageSize: 15 }}
          columns={[
            {
              title: '名稱', dataIndex: 'name',
              render: (name, record) => (
                <Space>
                  {record.is_dir ? <FolderOutlined style={{ color: '#faad14' }} /> : <FileOutlined style={{ color: '#1890ff' }} />}
                  <Text>{name}</Text>
                </Space>
              ),
            },
            { title: '大小', dataIndex: 'size_human', width: 100 },
            {
              title: '操作', width: 120,
              render: (_, record) => record.is_dir ? (
                <Button size="small" onClick={() => navigateAiFileBrowse(record.id, record.name)}>進入</Button>
              ) : (
                <Button
                  size="small"
                  type="primary"
                  loading={aiFileLoading}
                  onClick={async () => {
                    await loadGdriveFileContent(record.id, record.name);
                    setAiFilePickerOpen(false);
                  }}
                >
                  載入內容
                </Button>
              ),
            },
          ]}
        />
      </Modal>

      {/* Step Config Drawer */}
      <Drawer
        title={`步驟設定 - ${editingStep ? (STEP_TYPE_LABELS[editingStep.step_type] || editingStep.step_type) : ''}`}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={480}
        extra={
          <Button type="primary" onClick={handleSaveStepConfig}>
            儲存設定
          </Button>
        }
      >
        <Form form={stepForm} layout="vertical">
          <Form.Item
            name="name"
            label="步驟名稱"
            rules={[{ required: true, message: '請輸入步驟名稱' }]}
          >
            <Input placeholder="自訂步驟名稱" />
          </Form.Item>
          <Form.Item
            name="config"
            label="進階設定 (JSON)"
            rules={[{ required: true, message: '請輸入設定' }]}
          >
            <TextArea rows={12} placeholder='{"key": "value"}' style={{ fontFamily: 'monospace' }} />
          </Form.Item>
        </Form>
      </Drawer>

      {/* Add Step Drawer */}
      <Drawer
        title="新增流程步驟"
        open={addDrawerVisible}
        onClose={() => {
          setAddDrawerVisible(false);
          addForm.resetFields();
        }}
        width={400}
        extra={
          <Button type="primary" onClick={handleAddStep}>
            新增
          </Button>
        }
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            name="step_type"
            label="步驟類型"
            rules={[{ required: true, message: '請選擇步驟類型' }]}
          >
            <Select placeholder="選擇步驟類型">
              {Object.entries(STEP_TYPE_LABELS).map(([key, label]) => (
                <Option key={key} value={key}>
                  <Space>
                    {STEP_TYPE_ICONS[key]}
                    {label}
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="name" label="自訂名稱（選填）">
            <Input placeholder="留空則使用預設名稱" />
          </Form.Item>
        </Form>
      </Drawer>

      {/* Marketing Publish Modal */}
      <Modal
        title={
          <Space>
            {marketingPlatform === 'facebook' ? (
              <><FacebookOutlined style={{ color: '#1877F2' }} /> 發布到 Facebook</>
            ) : (
              <><GoogleOutlined style={{ color: '#4285F4' }} /> 發布到 Google Ads</>
            )}
          </Space>
        }
        open={marketingModalOpen}
        onCancel={() => { setMarketingModalOpen(false); setMarketingResult(null); }}
        footer={marketingResult?.ok ? [
          <Button key="close" onClick={() => { setMarketingModalOpen(false); setMarketingResult(null); }}>
            關閉
          </Button>,
        ] : [
          <Button key="cancel" onClick={() => { setMarketingModalOpen(false); setMarketingResult(null); }}>
            取消
          </Button>,
          <Button
            key="publish"
            type="primary"
            loading={marketingPublishing}
            onClick={handleMarketingPublish}
            icon={<SendOutlined />}
            style={marketingPlatform === 'facebook'
              ? { background: '#1877F2', borderColor: '#1877F2' }
              : { background: '#4285F4', borderColor: '#4285F4' }
            }
          >
            {marketingPublishing ? '發布中...' : '確認發布'}
          </Button>,
        ]}
        width={600}
      >
        {/* Success Result */}
        {marketingResult?.ok && (
          <Result
            status="success"
            title={marketingResult.message}
            subTitle={
              <Space direction="vertical" size="small">
                {marketingResult.post_url && (
                  <Text copyable={{ text: marketingResult.post_url }}>
                    貼文網址：{marketingResult.post_url}
                  </Text>
                )}
                {marketingResult.ads_manager_url && (
                  <a href={marketingResult.ads_manager_url} target="_blank" rel="noopener noreferrer">
                    前往 Facebook Ads Manager
                  </a>
                )}
                {marketingResult.google_ads_url && (
                  <a href={marketingResult.google_ads_url} target="_blank" rel="noopener noreferrer">
                    前往 Google Ads 後台
                  </a>
                )}
                {marketingResult.status === 'PAUSED' && (
                  <Tag color="orange">廣告狀態：暫停（需至後台審核啟用）</Tag>
                )}
              </Space>
            }
          />
        )}

        {/* Error Result */}
        {marketingResult && !marketingResult.ok && (
          <Alert
            type="error"
            message="發布失敗"
            description={marketingResult.error}
            showIcon
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setMarketingResult(null)}
          />
        )}

        {/* Facebook Form */}
        {marketingPlatform === 'facebook' && !marketingResult?.ok && (
          <Form form={marketingForm} layout="vertical">
            <Form.Item label="發布類型">
              <Radio.Group value={fbPostType} onChange={e => setFbPostType(e.target.value)}>
                <Radio.Button value="page_post">粉專貼文</Radio.Button>
                <Radio.Button value="ad">建立廣告</Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item name="message" label={fbPostType === 'page_post' ? '貼文內容' : '廣告文案'}>
              <Input.TextArea rows={4} placeholder="輸入文字內容，留空則自動從 AI 生成內容擷取" />
            </Form.Item>

            <Form.Item name="link" label="連結網址">
              <Input placeholder="留空則使用已發布的公開網址" prefix={<LinkOutlined />} />
            </Form.Item>

            {fbPostType === 'ad' && (
              <>
                <Form.Item name="campaign_name" label="廣告活動名稱">
                  <Input placeholder="SPO - 行銷活動" />
                </Form.Item>
                <Form.Item name="headline" label="廣告標題">
                  <Input placeholder="吸引點擊的標題" maxLength={60} />
                </Form.Item>
                <Form.Item name="description" label="廣告說明（選填）">
                  <Input placeholder="補充說明文字" />
                </Form.Item>
                <Form.Item name="daily_budget_cents" label="每日預算（美分）">
                  <InputNumber min={100} step={100} style={{ width: '100%' }} addonAfter="cents（1000 = $10 USD）" />
                </Form.Item>
                <Alert type="info" message="廣告將以「暫停」狀態建立，需至 Facebook Ads Manager 審核後手動啟用" style={{ marginBottom: 16 }} />
              </>
            )}
          </Form>
        )}

        {/* Google Ads Form */}
        {marketingPlatform === 'google_ads' && !marketingResult?.ok && (
          <Form form={marketingForm} layout="vertical">
            <Form.Item name="campaign_name" label="廣告活動名稱">
              <Input placeholder="SPO - 搜尋廣告活動" />
            </Form.Item>
            <Form.Item name="final_url" label="目標網址">
              <Input placeholder="廣告點擊後前往的網址" prefix={<LinkOutlined />} />
            </Form.Item>
            <Form.Item name="headlines" label="廣告標題（每行一個，最多 15 個，每個最多 30 字）">
              <Input.TextArea rows={3} placeholder={'標題一\n標題二\n標題三'} />
            </Form.Item>
            <Form.Item name="descriptions" label="廣告說明（每行一個，最多 4 個，每個最多 90 字）">
              <Input.TextArea rows={2} placeholder={'說明文字一\n說明文字二'} />
            </Form.Item>
            <Form.Item name="keywords" label="關鍵字（每行一個，最多 20 個）">
              <Input.TextArea rows={3} placeholder={'關鍵字一\n關鍵字二\n關鍵字三'} />
            </Form.Item>
            <Form.Item name="daily_budget_micros" label="每日預算（微單位）">
              <InputNumber min={1000000} step={1000000} style={{ width: '100%' }} addonAfter="micros（10,000,000 = $10 USD）" />
            </Form.Item>
            <Alert type="info" message="搜尋廣告將以「暫停」狀態建立，需至 Google Ads 後台審核後手動啟用" style={{ marginBottom: 16 }} />
          </Form>
        )}
      </Modal>

      {/* Import Google Slides Modal */}
      <Modal
        title={<Space><CloudUploadOutlined /> 匯入 Google 簡報 / PPTX</Space>}
        open={importModalOpen}
        onCancel={() => { setImportModalOpen(false); setImportResult(null); }}
        footer={importResult ? [
          <Button key="close" onClick={() => { setImportModalOpen(false); setImportResult(null); }}>關閉</Button>,
          importResult.is_published !== undefined && (
            <Button key="publish" type="primary" onClick={() => { handlePublish(importResult.id); setImportModalOpen(false); setImportResult(null); }}>
              發布為網頁
            </Button>
          ),
        ] : [
          <Button key="cancel" onClick={() => { setImportModalOpen(false); setImportResult(null); }}>取消</Button>,
          <Button key="import" type="primary" loading={importing} onClick={handleImportGSlides}
            icon={<CloudUploadOutlined />} disabled={!importFileId}>
            匯入並轉換為網頁
          </Button>,
        ]}
        width={700}
      >
        {importResult ? (
          <Result
            status="success"
            title={`簡報匯入成功！`}
            subTitle={`${importResult.title} — ${importResult.raw_content?.slides?.length || 0} 頁`}
            extra={[
              <div key="preview" style={{
                maxHeight: 300, overflow: 'auto', border: '1px solid #f0f0f0',
                borderRadius: 8, padding: 16, textAlign: 'left',
              }}>
                <div dangerouslySetInnerHTML={{ __html: (importResult.html_content || '').substring(0, 3000) }} />
              </div>,
            ]}
          />
        ) : (
          <div>
            <Alert
              type="info"
              showIcon
              message="從 Google Drive 匯入已製作好的簡報，直接轉換為網頁供客戶瀏覽"
              description="支援 Google Slides 和 .pptx 檔案，會自動擷取文字、表格和圖片。"
              style={{ marginBottom: 16 }}
            />

            {/* Step 1: File ID input */}
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>方法一：直接輸入 Google Drive 檔案 ID</Text>
              <Input
                placeholder="貼上 Google Drive 檔案 ID 或分享連結"
                value={importFileId}
                onChange={(e) => {
                  let val = e.target.value.trim();
                  const match = val.match(/\/d\/([a-zA-Z0-9_-]+)/);
                  if (match) val = match[1];
                  setImportFileId(val);
                }}
                addonBefore={<GoogleOutlined style={{ color: '#4285F4' }} />}
                allowClear
              />
              <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                從 Google Drive 分享連結中複製，例如：https://docs.google.com/presentation/d/<Text code>FILE_ID</Text>/edit
              </Text>
            </div>

            <Divider><Text type="secondary" style={{ fontSize: 12 }}>或</Text></Divider>

            {/* Step 2: Browse GDrive */}
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>方法二：從 Google Drive 瀏覽選擇</Text>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <Input
                  placeholder="資料夾 ID（留空使用情境已連結的資料夾）"
                  value={importBrowseFolderId}
                  onChange={(e) => setImportBrowseFolderId(e.target.value.trim())}
                  style={{ flex: 1 }}
                  allowClear
                />
                <Button
                  onClick={() => {
                    setImportBrowsePath([]);
                    loadImportBrowse(importBrowseFolderId || scenario?.gdrive_folder_id || null);
                  }}
                >
                  瀏覽
                </Button>
              </div>

              {importBrowseItems.length > 0 && (
                <>
                  <Breadcrumb style={{ marginBottom: 8 }}>
                    <Breadcrumb.Item>
                      <Button type="link" size="small" onClick={() => navigateImportBreadcrumb(-1)}>根目錄</Button>
                    </Breadcrumb.Item>
                    {importBrowsePath.map((p, idx) => (
                      <Breadcrumb.Item key={p.id}>
                        <Button type="link" size="small" onClick={() => navigateImportBreadcrumb(idx)}>{p.name}</Button>
                      </Breadcrumb.Item>
                    ))}
                  </Breadcrumb>
                  <Table
                    dataSource={importBrowseItems}
                    rowKey="id"
                    size="small"
                    loading={importBrowseLoading}
                    pagination={{ pageSize: 10 }}
                    columns={[
                      {
                        title: '名稱', dataIndex: 'name',
                        render: (name, record) => (
                          <Space>
                            {record.is_dir ? <FolderOutlined style={{ color: '#faad14' }} /> :
                              <FundProjectionScreenOutlined style={{ color: '#1890ff' }} />}
                            <Text>{name}</Text>
                          </Space>
                        ),
                      },
                      { title: '大小', dataIndex: 'size_human', width: 100 },
                      {
                        title: '操作', width: 120,
                        render: (_, record) => record.is_dir ? (
                          <Button size="small" onClick={() => navigateImportBrowse(record.id, record.name)}>進入</Button>
                        ) : (
                          <Button
                            size="small"
                            type="primary"
                            onClick={() => {
                              setImportFileId(record.id);
                              setImportTitle(record.name.replace(/\.pptx$/i, ''));
                              message.success(`已選擇: ${record.name}`);
                            }}
                          >
                            選擇
                          </Button>
                        ),
                      },
                    ]}
                  />
                </>
              )}
            </div>

            {/* Selected file info */}
            {importFileId && (
              <div style={{
                padding: '12px 16px', background: '#f6ffed',
                border: '1px solid #b7eb8f', borderRadius: 8, marginBottom: 16,
              }}>
                <Space>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <Text strong>已選擇檔案 ID:</Text>
                  <Text code copyable>{importFileId}</Text>
                </Space>
              </div>
            )}

            {/* Optional title override */}
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>自訂標題（選填）</Text>
              <Input
                placeholder="留空將自動使用簡報標題"
                value={importTitle}
                onChange={(e) => setImportTitle(e.target.value)}
              />
            </div>

            {/* Step type selection */}
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>歸屬步驟類型</Text>
              <Select
                value={importStepType}
                onChange={setImportStepType}
                style={{ width: '100%' }}
              >
                {Object.entries(STEP_TYPE_LABELS).map(([key, label]) => (
                  <Option key={key} value={key}>
                    <Space>{STEP_TYPE_ICONS[key]}{label}</Space>
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        )}
      </Modal>

      {/* Deploy as Marketing Site Modal */}
      <Modal
        title={<Space><GlobalOutlined /> 部署為行銷網站</Space>}
        open={deployModalOpen}
        onCancel={() => setDeployModalOpen(false)}
        footer={deployResult ? [
          <Button key="close" onClick={() => setDeployModalOpen(false)}>關閉</Button>,
          deployResult.public_url && <Button key="view" type="primary" onClick={() => window.open(`${window.location.origin}${deployResult.public_url}`, '_blank')}>查看網站</Button>,
          <Button key="manage" onClick={() => navigate(`/marketing-sites`)}>前往管理</Button>,
        ] : [
          <Button key="cancel" onClick={() => setDeployModalOpen(false)}>取消</Button>,
          existingSite && <Button key="refresh" type="primary" loading={deploying} onClick={handleRefreshSite}
            icon={<ThunderboltOutlined />} style={{ background: '#52c41a', borderColor: '#52c41a' }}>
            更新現有網站
          </Button>,
          <Button key="deploy" loading={deploying} onClick={handleDeploySite}
            icon={<GlobalOutlined />} type={existingSite ? 'default' : 'primary'}>
            {existingSite ? '部署新網站' : '確認部署'}
          </Button>,
        ]}
        width={550}
      >
        {!deployResult ? (
          <div>
            {existingSite && (
              <Alert
                type="success"
                showIcon
                message={`已有部署的網站：${existingSite.title || existingSite.slug}`}
                description={
                  <div>
                    <div>網址：<a href={`${window.location.origin}${existingSite.public_url || `/site/${existingSite.slug}`}`} target="_blank" rel="noreferrer">
                      {window.location.origin}{existingSite.public_url || `/site/${existingSite.slug}`}
                    </a></div>
                    <div style={{ marginTop: 4 }}>點擊「更新現有網站」將以最新生成的內容替換所有區塊，網址不變。</div>
                  </div>
                }
                style={{ marginBottom: 16 }}
              />
            )}
            {!existingSite && (
              <Alert
                type="info"
                showIcon
                message="將此情境的所有已生成內容部署為獨立行銷網站"
                description="系統會自動建立網站、產生專屬登入帳號，讓情境管理者可以獨立管理自己的行銷網站。"
                style={{ marginBottom: 16 }}
              />
            )}
            <Text strong>將包含的內容（已生成的步驟）：</Text>
            <div style={{ marginTop: 8 }}>
              {steps.filter(s => s.is_active).map(s => {
                const hasContent = stepContents[s.step_type];
                return (
                  <Tag key={s.id} color={hasContent ? 'green' : 'default'} style={{ marginBottom: 4 }}>
                    {hasContent ? '✓' : '○'} {STEP_TYPE_LABELS[s.step_type] || s.step_type}
                  </Tag>
                );
              })}
            </div>
            {steps.filter(s => s.is_active).length === 0 && (
              <Alert type="warning" message="此情境尚無啟用的步驟" style={{ marginTop: 8 }} />
            )}
          </div>
        ) : (
          <Result
            status="success"
            title={deployResult.refreshed ? "行銷網站已更新！" : "行銷網站部署成功！"}
            subTitle={deployResult.public_url && `網站網址：${window.location.origin}${deployResult.public_url}`}
            extra={[
              <div key="info" style={{ textAlign: 'left', background: '#f6ffed', padding: 16, borderRadius: 8, border: '1px solid #b7eb8f' }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text strong>網站網址：</Text>
                    <a href={deployResult.public_url} target="_blank" rel="noopener noreferrer">
                      {window.location.origin}{deployResult.public_url}
                    </a>
                  </div>
                  {!deployResult.refreshed && deployResult.owner_username && (
                    <>
                      <Divider style={{ margin: '8px 0' }} />
                      <Text strong>管理帳號（請妥善保存）：</Text>
                      <div>
                        <Text>帳號：</Text>
                        <Text code copyable>{deployResult.owner_username}</Text>
                      </div>
                      <div>
                        <Text>密碼：</Text>
                        <Text code copyable>{deployResult.owner_password}</Text>
                      </div>
                    </>
                  )}
                  {!deployResult.refreshed && deployResult.owner_username && (
                    <Alert type="warning" message="密碼僅顯示一次，請立即記錄" banner style={{ marginTop: 8 }} />
                  )}
                </Space>
              </div>,
            ]}
          />
        )}
      </Modal>

      {/* Blog Multi-Platform Publish Modal */}
      <Modal
        title={<Space><EditOutlined style={{ color: '#52c41a' }} /> 發布部落格（多平台）</Space>}
        open={blogPublishModalOpen}
        onCancel={() => { setBlogPublishModalOpen(false); setBlogPublishResult(null); }}
        footer={blogPublishResult?.ok ? [
          <Button key="close" onClick={() => { setBlogPublishModalOpen(false); setBlogPublishResult(null); }}>
            關閉
          </Button>,
        ] : [
          <Button key="cancel" onClick={() => { setBlogPublishModalOpen(false); setBlogPublishResult(null); }}>
            取消
          </Button>,
          <Button
            key="publish"
            type="primary"
            loading={blogPublishing}
            onClick={handleBlogPublish}
            icon={<SendOutlined />}
            style={{ background: '#52c41a', borderColor: '#52c41a' }}
            disabled={blogSelectedPlatforms.length === 0}
          >
            {blogPublishing ? '發布中...' : `發布到 ${blogSelectedPlatforms.length} 個平台`}
          </Button>,
        ]}
        width={650}
      >
        {/* Success Result */}
        {blogPublishResult?.ok && (
          <Result
            status="success"
            title="部落格發布完成！"
            subTitle={
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                {(blogPublishResult.results || []).map((r, idx) => (
                  <div key={idx} style={{
                    padding: '8px 12px',
                    background: r.ok ? '#f6ffed' : '#fff2f0',
                    border: `1px solid ${r.ok ? '#b7eb8f' : '#ffccc7'}`,
                    borderRadius: 6,
                  }}>
                    <Space>
                      {r.ok ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                      <Text strong>{r.platform}</Text>
                      <Text>{r.message || r.error || ''}</Text>
                    </Space>
                    {r.url && (
                      <div style={{ marginTop: 4, marginLeft: 22 }}>
                        <a href={r.url} target="_blank" rel="noopener noreferrer">{r.url}</a>
                      </div>
                    )}
                    {r.manual && (
                      <div style={{ marginTop: 4, marginLeft: 22 }}>
                        <Text type="secondary" copyable={{ text: r.generated_content || r.generated_caption || '' }}>
                          複製內容手動發布
                        </Text>
                      </div>
                    )}
                  </div>
                ))}
                {blogPublishResult.blog_url && (
                  <div style={{ marginTop: 8 }}>
                    <Text strong>SPO 部落格：</Text>
                    <a href={blogPublishResult.blog_url} target="_blank" rel="noopener noreferrer">
                      查看文章
                    </a>
                  </div>
                )}
              </Space>
            }
          />
        )}

        {/* Error Result */}
        {blogPublishResult && !blogPublishResult.ok && !blogPublishResult.results && (
          <Alert
            type="error"
            message="發布失敗"
            description={blogPublishResult.error}
            showIcon
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setBlogPublishResult(null)}
          />
        )}

        {/* Platform Selection & Form */}
        {!blogPublishResult?.ok && (
          <div>
            <Text strong style={{ display: 'block', marginBottom: 12 }}>選擇發布平台</Text>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8, marginBottom: 16 }}>
              {Object.entries(blogPlatforms).map(([key, info]) => (
                <div
                  key={key}
                  onClick={() => {
                    setBlogSelectedPlatforms(prev =>
                      prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]
                    );
                  }}
                  style={{
                    padding: '10px 14px',
                    border: `2px solid ${blogSelectedPlatforms.includes(key) ? '#52c41a' : '#d9d9d9'}`,
                    borderRadius: 8,
                    cursor: 'pointer',
                    background: blogSelectedPlatforms.includes(key) ? '#f6ffed' : '#fff',
                    transition: 'all 0.2s',
                  }}
                >
                  <Space>
                    {blogSelectedPlatforms.includes(key) ?
                      <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                      <div style={{ width: 14, height: 14, border: '2px solid #d9d9d9', borderRadius: '50%' }} />
                    }
                    <div>
                      <Text strong style={{ fontSize: 13 }}>{info.label}</Text>
                      {!info.ok && !info.manual && (
                        <div><Text type="danger" style={{ fontSize: 11 }}>未設定憑證</Text></div>
                      )}
                      {info.manual && (
                        <div><Text type="secondary" style={{ fontSize: 11 }}>手動發布</Text></div>
                      )}
                      {info.ok && !info.manual && (
                        <div><Text type="success" style={{ fontSize: 11 }}>已設定</Text></div>
                      )}
                    </div>
                  </Space>
                </div>
              ))}
              {Object.keys(blogPlatforms).length === 0 && (
                <Alert type="info" message="正在載入平台設定..." showIcon />
              )}
            </div>

            <Form form={blogPublishForm} layout="vertical">
              <Form.Item name="message" label="社交平台文案（用於 Facebook / Threads / Twitter 等）">
                <Input.TextArea rows={3} placeholder="留空則使用 AI 生成的社交文案" />
              </Form.Item>
              <Form.Item name="tags" label="標籤（逗號分隔）">
                <Input placeholder="例：AI, 伺服器, 數位轉型" />
              </Form.Item>
              <Form.Item name="category" label="部落格分類（選填）">
                <Input placeholder="例：技術文章" />
              </Form.Item>
            </Form>

            <Alert
              type="info"
              showIcon
              message="發布說明"
              description={
                <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                  <li><Text strong>SPO 部落格</Text>：自動建立文章到平台部落格系統</li>
                  <li><Text strong>Facebook</Text>：發布到粉絲專頁貼文</li>
                  <li><Text strong>Instagram</Text>：需配合圖片，會先生成文案</li>
                  <li><Text strong>Threads</Text>：發布文字貼文</li>
                  <li><Text strong>LinkedIn</Text>：發布到個人/公司頁面</li>
                  <li><Text strong>Twitter / X</Text>：發布推文（280字限制）</li>
                  <li><Text strong>Medium</Text>：建立草稿文章，需至 Medium 確認</li>
                  <li><Text strong>小紅書</Text>：生成文案，需手動複製到 App 發布</li>
                </ul>
              }
            />
          </div>
        )}
      </Modal>

      {/* Email Campaign Schedule Modal */}
      <Modal
        title={<Space><MailOutlined style={{ color: '#1890ff' }} /> 排程發送郵件</Space>}
        open={emailCampaignModalOpen}
        onCancel={() => { setEmailCampaignModalOpen(false); setEmailCampaignResult(null); }}
        footer={emailCampaignResult?.success ? [
          <Button key="close" onClick={() => { setEmailCampaignModalOpen(false); setEmailCampaignResult(null); }}>
            關閉
          </Button>,
        ] : [
          <Button key="cancel" onClick={() => { setEmailCampaignModalOpen(false); setEmailCampaignResult(null); }}>
            取消
          </Button>,
          <Button
            key="schedule"
            type="primary"
            loading={emailCampaignCreating}
            onClick={() => handleCreateEmailCampaign(false)}
            icon={<CalendarOutlined />}
          >
            排程發送
          </Button>,
          <Button
            key="send-now"
            loading={emailCampaignCreating}
            onClick={() => handleCreateEmailCampaign(true)}
            icon={<SendOutlined />}
            danger
          >
            立即發送
          </Button>,
        ]}
        width={600}
      >
        {emailCampaignResult?.success ? (
          <Result
            status="success"
            title={emailCampaignResult.scheduled_at ? '郵件已排程！' : '操作完成！'}
            subTitle={emailCampaignResult.scheduled_at
              ? `排程時間：${emailCampaignResult.scheduled_at}`
              : `活動 ID: ${emailCampaignResult.campaign_id}`
            }
          />
        ) : (
          <div>
            {/* Version selector */}
            <div style={{ marginBottom: 20 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>選擇郵件版本</Text>
              <Radio.Group value={emailVersionTab} onChange={e => setEmailVersionTab(e.target.value)} buttonStyle="solid" size="large">
                <Radio.Button value="sales" style={{ borderColor: emailVersionTab === 'sales' ? '#e74c3c' : undefined }}>
                  🔥 銷售版
                </Radio.Button>
                <Radio.Button value="marketing" style={{ borderColor: emailVersionTab === 'marketing' ? '#3498db' : undefined }}>
                  📢 行銷版
                </Radio.Button>
                <Radio.Button value="event" style={{ borderColor: emailVersionTab === 'event' ? '#2ecc71' : undefined }}>
                  🎉 活動版
                </Radio.Button>
              </Radio.Group>
              {aiResult?.raw_content?.versions?.[emailVersionTab] && (
                <div style={{ marginTop: 8, padding: '6px 12px', background: '#f5f5f5', borderRadius: 6 }}>
                  <Text type="secondary">主旨：</Text>
                  <Text strong>{aiResult.raw_content.versions[emailVersionTab].subject}</Text>
                </div>
              )}
            </div>

            <Form form={emailCampaignForm} layout="vertical">
              <Form.Item name="campaign_name" label="活動名稱（選填）">
                <Input placeholder="留空將自動命名" />
              </Form.Item>
              <Form.Item name="contact_list_id" label="聯絡人名單" rules={[{ required: true, message: '請選擇聯絡人名單' }]}>
                <Select
                  placeholder="選擇要發送的聯絡人名單"
                  loading={emailContactListsLoading}
                  notFoundContent={emailContactListsLoading ? <Spin size="small" /> : '尚無聯絡人名單'}
                >
                  {emailContactLists.map(list => (
                    <Option key={list.id} value={list.id}>
                      {list.name} ({list.member_count || 0} 人)
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="scheduled_at" label="排程時間（選填，留空則為草稿或立即發送）">
                <DatePicker
                  showTime={{ format: 'HH:mm' }}
                  format="YYYY-MM-DD HH:mm"
                  placeholder="選擇排程發送時間"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Form>

            <Alert
              type="info"
              showIcon
              message="發送說明"
              description={
                <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                  <li><Text strong>排程發送</Text>：設定排程時間後系統將自動在指定時間發送</li>
                  <li><Text strong>立即發送</Text>：立即將郵件送出到所有名單收件人</li>
                  <li>系統每分鐘檢查排程，發送包含開信追蹤與點擊追蹤</li>
                  <li>每 50 封自動重連 SMTP 確保穩定送達</li>
                </ul>
              }
            />
          </div>
        )}
      </Modal>

      {/* Blog Crawler Modal */}
      <Modal
        title={<Space><SearchOutlined style={{ color: '#1890ff' }} /> 爬取相關部落格文章</Space>}
        open={blogCrawlModalOpen}
        onCancel={() => setBlogCrawlModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setBlogCrawlModalOpen(false)}>關閉</Button>,
          <Button
            key="generate"
            type="primary"
            icon={<ThunderboltOutlined />}
            loading={blogCrawlGenerating}
            disabled={selectedCrawledIds.length === 0}
            onClick={handleGenerateFromCrawled}
          >
            根據選取文章生成新部落格
          </Button>,
        ]}
        width={700}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>輸入部落格網址（支援 RSS / HTML 頁面）</Text>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              value={blogCrawlUrl}
              onChange={e => setBlogCrawlUrl(e.target.value)}
              placeholder="例：https://blog.example.com 或 https://example.com/feed"
              onPressEnter={handleBlogCrawl}
            />
            <Button type="primary" icon={<SearchOutlined />} loading={blogCrawling} onClick={handleBlogCrawl}>
              爬取
            </Button>
          </Space.Compact>
        </div>

        {crawledArticles.length > 0 && (
          <div>
            <Text strong style={{ marginBottom: 8, display: 'block' }}>
              爬取到 {crawledArticles.length} 篇文章（已選取 {selectedCrawledIds.length} 篇作為參考素材）
            </Text>
            <div style={{ maxHeight: 400, overflow: 'auto' }}>
              {crawledArticles.map((article, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '10px 14px',
                    border: `1px solid ${selectedCrawledIds.includes(idx) ? '#1890ff' : '#f0f0f0'}`,
                    borderRadius: 8,
                    marginBottom: 8,
                    cursor: 'pointer',
                    background: selectedCrawledIds.includes(idx) ? '#e6f7ff' : '#fff',
                  }}
                  onClick={() => {
                    setSelectedCrawledIds(prev =>
                      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
                    );
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    <input type="checkbox" checked={selectedCrawledIds.includes(idx)} readOnly style={{ marginRight: 8 }} />
                    {article.title || '(無標題)'}
                  </div>
                  {article.excerpt && <div style={{ color: '#666', fontSize: 13, marginBottom: 4 }}>{article.excerpt.substring(0, 150)}...</div>}
                  <Space size="small">
                    {article.published_date && <Tag color="blue">{article.published_date}</Tag>}
                    {article.author && <Tag>{article.author}</Tag>}
                    {article.article_url && <a href={article.article_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}><Text type="secondary" style={{ fontSize: 12 }}>查看原文</Text></a>}
                  </Space>
                </div>
              ))}
            </div>
          </div>
        )}

        {crawledArticles.length === 0 && !blogCrawling && (
          <Alert
            type="info"
            showIcon
            message="使用說明"
            description={
              <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                <li>輸入任何部落格網址，系統會自動偵測 RSS / Atom feed 或直接解析 HTML</li>
                <li>爬取後勾選想要參考的文章，AI 將根據這些文章的主題和風格生成新的原創部落格</li>
                <li>生成的文章會自動融合您的產品簡報內容，確保行銷導向</li>
              </ul>
            }
          />
        )}
      </Modal>
    </div>
  );
};

export default ScenarioBuilderPage;
